@file:Suppress("unused", "BooleanLiteralArgument")

package world.phantasmal.psolib.asm

import world.phantasmal.psolib.fileFormats.quest.Version

class OpcodeTables(
    val byCode: Array<Opcode?>,
    val byCodeF8: Array<Opcode?>,
    val byCodeF9: Array<Opcode?>,
)

val OP_NOP = Opcode(
    code = 0x00,
    mnemonic = "nop",
    doc = "No operation, does nothing.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_RET = Opcode(
    code = 0x01,
    mnemonic = "ret",
    doc = "Returns control to caller.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SYNC = Opcode(
    code = 0x02,
    mnemonic = "sync",
    doc = "Yields control for the rest of the current frame. Execution will continue the following frame.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_EXIT = Opcode(
    code = 0x03,
    mnemonic = "exit",
    doc = "Exits entirely",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_THREAD = Opcode(
    code = 0x04,
    mnemonic = "thread",
    doc = "Starts a new thread. Thread execution will start at the given label.\nOften used to check a register every frame. Make sure to yield control with sync when looping.",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_VA_START_V3_V4 = Opcode(
    code = 0x05,
    mnemonic = "va_start",
    doc = "Initializes a variable argument list.\nMake sure to call va_end after va_start and va_call.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.None,
)
val OP_VA_END_V3_V4 = Opcode(
    code = 0x06,
    mnemonic = "va_end",
    doc = "Restores the registers overwritten by arg_push* instructions.\nCalled after va_call.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.None,
)
val OP_VA_CALL_V3_V4 = Opcode(
    code = 0x07,
    mnemonic = "va_call",
    doc = "Calls the variable argument function at the given label.\nCalled after initializing the argument list with va_start and pushing arguments onto the stack with arg_push* instructions. Make sure to call va_end afterwards.",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_LET = Opcode(
    code = 0x08,
    mnemonic = "let",
    doc = "Sets a register to a given register's value.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_LETI = Opcode(
    code = 0x09,
    mnemonic = "leti",
    doc = "Sets a register to the given value.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_LETA_V0_V2 = Opcode(
    code = 0x0A,
    mnemonic = "leta",
    doc = "Sets a register to the memory address of another register. V0_V2 form; not used by Sega on DC NTE. (newserv QuestScript.cc:345)",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_LETB_V3_V4 = Opcode(
    code = 0x0A,
    mnemonic = "letb",
    doc = "Sets a register to the given byte value.",
    params = listOf(
        Param(RegType(listOf(
            Param(ByteType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(ByteType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_LETW = Opcode(
    code = 0x0B,
    mnemonic = "letw",
    doc = "Sets a register to the given value.",
    params = listOf(
        Param(RegType(listOf(
            Param(ShortType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(ShortType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_LETA = Opcode(
    code = 0x0C,
    mnemonic = "leta",
    doc = "Sets a register to the memory address of a given register. Not used by Sega.",
    params = listOf(
        Param(RegType(listOf(
            Param(PointerType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_LETO = Opcode(
    code = 0x0D,
    mnemonic = "leto",
    doc = "Sets a register to the memory address of the given label. Not used by Sega.",
    params = listOf(
        Param(RegType(listOf(
            Param(PointerType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(LabelType.Instance, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET = Opcode(
    code = 0x10,
    mnemonic = "set",
    doc = "Sets a register to 1.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CLEAR = Opcode(
    code = 0x11,
    mnemonic = "clear",
    doc = "Sets a register to 0.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_REV = Opcode(
    code = 0x12,
    mnemonic = "rev",
    doc = "Sets a register to 1 if its current value is 0, otherwise sets it to 0.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GSET = Opcode(
    code = 0x13,
    mnemonic = "gset",
    doc = "Sets flagA to 1. Sends 6x75.",
    params = listOf(
        Param(ShortType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GCLEAR = Opcode(
    code = 0x14,
    mnemonic = "gclear",
    doc = "Clears flagA to 0. Sends 6x75 on BB, but does not send anything on other versions.",
    params = listOf(
        Param(ShortType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GREV = Opcode(
    code = 0x15,
    mnemonic = "grev",
    doc = "Inverts flagA. Like the above two opcodes, sends 6x75 if the flag is set by this opcode. Only BB sends 6x75 if the flag is cleared by this opcode.",
    params = listOf(
        Param(ShortType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GLET = Opcode(
    code = 0x16,
    mnemonic = "glet",
    doc = "If regB is nonzero, sets flagA; otherwise, clears it",
    params = listOf(
        Param(ShortType, "flag", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GGET = Opcode(
    code = 0x17,
    mnemonic = "gget",
    doc = "Sets a register to value of the given flag.",
    params = listOf(
        Param(ShortType, null, null, false, false),
        Param(RegType(listOf(
            Param(ShortType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ADD = Opcode(
    code = 0x18,
    mnemonic = "add",
    doc = "regA += regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ADDI = Opcode(
    code = 0x19,
    mnemonic = "addi",
    doc = "regA += valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SUB = Opcode(
    code = 0x1A,
    mnemonic = "sub",
    doc = "regA -= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SUBI = Opcode(
    code = 0x1B,
    mnemonic = "subi",
    doc = "regA -= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MUL = Opcode(
    code = 0x1C,
    mnemonic = "mul",
    doc = "regA *= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MULI = Opcode(
    code = 0x1D,
    mnemonic = "muli",
    doc = "regA *= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DIV = Opcode(
    code = 0x1E,
    mnemonic = "div",
    doc = "regA /= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DIVI = Opcode(
    code = 0x1F,
    mnemonic = "divi",
    doc = "regA /= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AND = Opcode(
    code = 0x20,
    mnemonic = "and",
    doc = "regA &= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ANDI = Opcode(
    code = 0x21,
    mnemonic = "andi",
    doc = "regA &= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_OR = Opcode(
    code = 0x22,
    mnemonic = "or",
    doc = "regA |= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ORI = Opcode(
    code = 0x23,
    mnemonic = "ori",
    doc = "regA |= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_XOR = Opcode(
    code = 0x24,
    mnemonic = "xor",
    doc = "regA ^= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_XORI = Opcode(
    code = 0x25,
    mnemonic = "xori",
    doc = "regA ^= valueB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MOD = Opcode(
    code = 0x26,
    mnemonic = "mod",
    doc = "regA %= regB Note: This does signed division, so if the value is negative, you might get unexpected results.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MODI = Opcode(
    code = 0x27,
    mnemonic = "modi",
    doc = "regA %= valueB Note: Unlike mod, this does unsigned division.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP = Opcode(
    code = 0x28,
    mnemonic = "jmp",
    doc = "Jumps to labelA",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CALL = Opcode(
    code = 0x29,
    mnemonic = "call",
    doc = "Pushes the script offset immediately after this opcode and jumps to labelA Note: This opcode doesn't directly clear the args list, but we assume during disassembly that the code being called does so.",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_ON = Opcode(
    code = 0x2A,
    mnemonic = "jmp_on",
    doc = "If all values in regsB are nonzero, jumps to labelA",
    params = listOf(
        Param(ILabelType, null, null, false, false),
        Param(RegVarType, null, null, true, false),
    ),
    stack = null,
    varargs = true,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_OFF = Opcode(
    code = 0x2B,
    mnemonic = "jmp_off",
    doc = "If all values in regsB are zero, jumps to labelA",
    params = listOf(
        Param(ILabelType, null, null, false, false),
        Param(RegVarType, null, null, true, false),
    ),
    stack = null,
    varargs = true,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_E = Opcode(
    code = 0x2C,
    mnemonic = "jmp_=",
    doc = "If regA == regB, jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_E = Opcode(
    code = 0x2D,
    mnemonic = "jmpi_=",
    doc = "If regA == valueB, jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_NE = Opcode(
    code = 0x2E,
    mnemonic = "jmp_!=",
    doc = "If regA != regB, jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_NE = Opcode(
    code = 0x2F,
    mnemonic = "jmpi_!=",
    doc = "If regA != valueB, jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMP_G = Opcode(
    code = 0x30,
    mnemonic = "ujmp_>",
    doc = "If regA > regB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMPI_G = Opcode(
    code = 0x31,
    mnemonic = "ujmpi_>",
    doc = "If regA > valueB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_G = Opcode(
    code = 0x32,
    mnemonic = "jmp_>",
    doc = "If regA > regB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_G = Opcode(
    code = 0x33,
    mnemonic = "jmpi_>",
    doc = "If regA > valueB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMP_L = Opcode(
    code = 0x34,
    mnemonic = "ujmp_<",
    doc = "If regA < regB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMPI_L = Opcode(
    code = 0x35,
    mnemonic = "ujmpi_<",
    doc = "If regA < valueB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_L = Opcode(
    code = 0x36,
    mnemonic = "jmp_<",
    doc = "If regA < regB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_L = Opcode(
    code = 0x37,
    mnemonic = "jmpi_<",
    doc = "If regA < valueB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMP_GE = Opcode(
    code = 0x38,
    mnemonic = "ujmp_>=",
    doc = "If regA >= regB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMPI_GE = Opcode(
    code = 0x39,
    mnemonic = "ujmpi_>=",
    doc = "If regA >= valueB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_GE = Opcode(
    code = 0x3A,
    mnemonic = "jmp_>=",
    doc = "If regA >= regB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_GE = Opcode(
    code = 0x3B,
    mnemonic = "jmpi_>=",
    doc = "If regA >= valueB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMP_LE = Opcode(
    code = 0x3C,
    mnemonic = "ujmp_<=",
    doc = "If regA <= regB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UJMPI_LE = Opcode(
    code = 0x3D,
    mnemonic = "ujmpi_<=",
    doc = "If regA <= valueB (unsigned), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMP_LE = Opcode(
    code = 0x3E,
    mnemonic = "jmp_<=",
    doc = "If regA <= regB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_JMPI_LE = Opcode(
    code = 0x3F,
    mnemonic = "jmpi_<=",
    doc = "If regA <= valueB (signed), jumps to labelC",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SWITCH_JMP = Opcode(
    code = 0x40,
    mnemonic = "switch_jmp",
    doc = "Jumps to labelsB[regA]; if regA is out of range of labelsB, does nothing",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelVarType, null, null, false, false),
    ),
    stack = null,
    varargs = true,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SWITCH_CALL = Opcode(
    code = 0x41,
    mnemonic = "switch_call",
    doc = "Calls labelsB[regA]; if regA is out of range of labelsB, does nothing Note: This opcode doesn't directly clear the args list, but we assume during disassembly that the code being called does so.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(ILabelVarType, null, null, false, false),
    ),
    stack = null,
    varargs = true,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NOP_42_V0_V2 = Opcode(
    code = 0x42,
    mnemonic = "nop_42",
    doc = "Does nothing. V0_V2 inline form with one unused 32-bit argument. (newserv QuestScript.cc:517)",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_STACK_PUSH_V3_V4 = Opcode(
    code = 0x42,
    mnemonic = "stack_push",
    doc = "Pushes the value of the given register onto the argument stack.",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_STACK_POP_V3_V4 = Opcode(
    code = 0x43,
    mnemonic = "stack_pop",
    doc = "Pops a value from the stack and puts it into regA",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_STACK_PUSHM_V3_V4 = Opcode(
    code = 0x44,
    mnemonic = "stack_pushm",
    doc = "Pushes the values of an arbitrary amount of registers onto the stack.",
    params = listOf(
        Param(RegType(null, 1), null, null, true, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_STACK_POPM_V3_V4 = Opcode(
    code = 0x45,
    mnemonic = "stack_popm",
    doc = "Pops an arbitrary amount of values from the stack and writes them to registers.",
    params = listOf(
        Param(RegType(null, 1), null, null, false, true),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHR_V3_V4 = Opcode(
    code = 0x48,
    mnemonic = "arg_pushr",
    doc = "Pushes the value of the given register onto the stack.",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHL_V3_V4 = Opcode(
    code = 0x49,
    mnemonic = "arg_pushl",
    doc = "Pushes the given value onto the stack.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHB_V3_V4 = Opcode(
    code = 0x4A,
    mnemonic = "arg_pushb",
    doc = "Pushes the given value onto the stack.",
    params = listOf(
        Param(ByteType, null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHW_V3_V4 = Opcode(
    code = 0x4B,
    mnemonic = "arg_pushw",
    doc = "Pushes the given value onto the stack.",
    params = listOf(
        Param(ShortType, null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHA_V3_V4 = Opcode(
    code = 0x4C,
    mnemonic = "arg_pusha",
    doc = "Pushes the memory address of the given register onto the stack. Not used by Sega.",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHO_V3_V4 = Opcode(
    code = 0x4D,
    mnemonic = "arg_pusho",
    doc = "Pushes the memory address of the given label onto the stack. Not used by Sega.",
    params = listOf(
        Param(LabelType.Instance, null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_ARG_PUSHS_V3_V4 = Opcode(
    code = 0x4E,
    mnemonic = "arg_pushs",
    doc = "Pushes the given value onto the stack.",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Push,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_UNKNOWN_4F = Opcode(
    code = 0x4F,
    mnemonic = "unknown_4f",
    doc = null,
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MESSAGE = Opcode(
    code = 0x50,
    mnemonic = "message",
    doc = "Creates dialogue with an object/NPC (valueA) starting with message strB",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_LIST = Opcode(
    code = 0x51,
    mnemonic = "list",
    doc = "Used to display a list of items and retrieve the item selected by the player.\nList items should be seperated by newlines. The selected item's index will be written to the given register.",
    params = listOf(
        Param(RegType(listOf(
            Param(ByteType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(StringType, "list", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_FADEIN = Opcode(
    code = 0x52,
    mnemonic = "fadein",
    doc = "Fades from black",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_FADEOUT = Opcode(
    code = 0x53,
    mnemonic = "fadeout",
    doc = "Fades to black",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SE = Opcode(
    code = 0x54,
    mnemonic = "se",
    doc = "Plays a sound effect",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BGM = Opcode(
    code = 0x55,
    mnemonic = "bgm",
    doc = "Plays a fanfare (clear.adx if valueA is 0, or miniclear.adx if it's 1). There is no bounds check on this; values other than 0 or 1 will result in undefined behavior.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NOP_56 = Opcode(
    code = 0x56,
    mnemonic = "nop_56",
    doc = "Does nothing",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NOP_57 = Opcode(
    code = 0x57,
    mnemonic = "nop_57",
    doc = "Does nothing",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_ENABLE = Opcode(
    code = 0x58,
    mnemonic = "enable",
    doc = "Does nothing",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DISABLE = Opcode(
    code = 0x59,
    mnemonic = "disable",
    doc = "Does nothing",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_WINDOW_MSG = Opcode(
    code = 0x5A,
    mnemonic = "window_msg",
    doc = "Displays a message. Special tokens are interpolated within the string; these special tokens are: <rXX> => value of rXX as %d (signed integer) <fXX> => value of rXX as %f (floating-point) (v3 and later) <color X> => changes text color like \$CX would (supported on 11/2000 and later); X must be numeric and in the range 0-7, so <color 8>, <color 9>, and <color G> do not work (though \\tC8, \\tC9, and \\tCG can be used directly in the text, and do work) <cr> => newline <hero name> or <name hero> => character's name <hero job> or <name job> => character's class <time> => always \"01:12\" (seems like an oversight that was never fixed) <award item> => name of the chosen challenge mode reward (v2 and later) <challenge title> => character's challenge rank text (v2 and later) <pl_name> => name of character selected with get_pl_name (v2 and later) <pl_job> => class of character selected with get_pl_job (v2 and later) <last_word> => challenge mode grave message (v2 and later) <team_name> => name of the game (set by 8A command) (v2 and later) <last_chat> => last chat message (v2 and later) <meseta_slot_prize> => the description of the last item sent by the server in a 6xE3 command (BB only)",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ADD_MSG = Opcode(
    code = 0x5B,
    mnemonic = "add_msg",
    doc = "Adds a message to an existing message (or window_msg). Tokens are interpolated as for window_msg.",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_MESEND = Opcode(
    code = 0x5C,
    mnemonic = "mesend",
    doc = "Closes the current message box",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GETTIME = Opcode(
    code = 0x5D,
    mnemonic = "gettime",
    doc = "Gets the current time, in seconds since 00:00:00 on 1 January 2000",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_WINEND = Opcode(
    code = 0x5E,
    mnemonic = "winend",
    doc = "Closes a window_msg",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NPC_CRT_V3 = Opcode(
    code = 0x60,
    mnemonic = "npc_crt_v3",
    doc = "Creates a networked follower NPC. Sends 6x69 with command = 0. On V4 the first argument is the owner/follow player slot, not an alive/dead state.",
    params = listOf(
        Param(IntType, "owner player slot", "Player slot that owns/controls the follower NPC.", false, false),
        Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_STOP = Opcode(
    code = 0x61,
    mnemonic = "npc_stop",
    doc = "On V4, stops the networked NPC in the specified player slot and clears its follow owner.",
    params = listOf(
        Param(IntType, "NPC player slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_PLAY = Opcode(
    code = 0x62,
    mnemonic = "npc_play",
    doc = "On V4, resumes challenge-style NPCs and makes them follow the specified owner player slot. The argument is not an NPC ID.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_KILL = Opcode(
    code = 0x63,
    mnemonic = "npc_kill",
    doc = "On V4, destroys the NPC in the specified player slot and clears its tracking state.",
    params = listOf(
        Param(IntType, "NPC player slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_NONT = Opcode(
    code = 0x64,
    mnemonic = "npc_nont",
    doc = "On V4, disables automatic contextual NPC chat bubbles.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NPC_TALK = Opcode(
    code = 0x65,
    mnemonic = "npc_talk",
    doc = "On V4, enables automatic contextual NPC chat bubbles.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NPC_CRP_V0_V2 = Opcode(
    code = 0x66,
    mnemonic = "npc_crp",
    doc = "Creates an NPC as client ID 1. V0_V2 inline form: a 1-byte base register for 6 consecutive registers, followed by one ignored 32-bit argument.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "initial state", "initial state (0 = alive, 1 = dead, 2 = invisible text box)", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Ignored 32-bit argument present in V0_V2 form but not V3_V4.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRP_V3_V3_V4 = Opcode(
    code = 0x66,
    mnemonic = "npc_crp_v3",
    doc = "Creates a networked follower NPC at a position. Sends 6x69 with command = 0. The V4 receiver interprets register 4 as the owner/follow player slot.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "owner player slot", "Player slot that owns/controls the follower NPC.", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CREATE_PIPE = Opcode(
    code = 0x68,
    mnemonic = "create_pipe",
    doc = "Creates a pipe. valueA is client ID",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_HPSTAT_V3 = Opcode(
    code = 0x69,
    mnemonic = "p_hpstat_v3",
    doc = "Checks player HP, but not in a straightforward manner: sets regA to 1 if (current_hp / max_hp) < (1 / valueA) for the client specified by valueB. Sets regA to 0 otherwise.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_DEAD_V3 = Opcode(
    code = 0x6A,
    mnemonic = "p_dead_v3",
    doc = "Sets regA to 1 if player in slot (valueB) is dead, or 0 if alive.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_DISABLEWARP = Opcode(
    code = 0x6B,
    mnemonic = "p_disablewarp",
    doc = "Disables/enables telepipes/Ryuker",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_ENABLEWARP = Opcode(
    code = 0x6C,
    mnemonic = "p_enablewarp",
    doc = "Disables/enables telepipes/Ryuker",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_MOVE_V0_V2 = Opcode(
    code = 0x6D,
    mnemonic = "p_move",
    doc = "Moves a player. V0_V2 inline form with 5 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:655)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "destination X", null, true, false),
            Param(IntType, "destination Y", null, true, false),
            Param(IntType, "destination Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V0_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_P_MOVE_V3_V3_V4 = Opcode(
    code = 0x6D,
    mnemonic = "p_move_v3",
    doc = "Moves a player. (newserv QuestScript.cc:656)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "destination X", null, true, false),
            Param(IntType, "destination Y", null, true, false),
            Param(IntType, "destination Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_P_LOOK = Opcode(
    code = 0x6E,
    mnemonic = "p_look",
    doc = "Causes the player with client ID valueA to look at an unspecified other player. The specified player looks at the player with the lowest client ID (except for the specified player). TODO: TObjPlayer::state is involved in determining which player to look at; figure out exactly what this does",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_ACTION_DISABLE = Opcode(
    code = 0x70,
    mnemonic = "p_action_disable",
    doc = "Disables/enables attacks for all players",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_ACTION_ENABLE = Opcode(
    code = 0x71,
    mnemonic = "p_action_enable",
    doc = "Disables/enables attacks for all players",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISABLE_MOVEMENT1 = Opcode(
    code = 0x72,
    mnemonic = "disable_movement1",
    doc = "Disables/enables movement for the given player",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ENABLE_MOVEMENT1 = Opcode(
    code = 0x73,
    mnemonic = "enable_movement1",
    doc = "Disables/enables movement for the given player",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_NONCOL = Opcode(
    code = 0x74,
    mnemonic = "p_noncol",
    doc = "These appear to do nothing at all. On v3, they set and clear a flag that is never read. On DC NTE and v1, code exists to read this flag, but it belongs to an object that appears to never be constructed anywhere.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_COL = Opcode(
    code = 0x75,
    mnemonic = "p_col",
    doc = "These appear to do nothing at all. On v3, they set and clear a flag that is never read. On DC NTE and v1, code exists to read this flag, but it belongs to an object that appears to never be constructed anywhere.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_SETPOS = Opcode(
    code = 0x76,
    mnemonic = "p_setpos",
    doc = "Sets a player's position.",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
        ), 1), null, "Coordinates and rotation around y-axis.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_RETURN_GUILD = Opcode(
    code = 0x77,
    mnemonic = "p_return_guild",
    doc = "Returns players to the Hunter's Guild counter.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_P_TALK_GUILD = Opcode(
    code = 0x78,
    mnemonic = "p_talk_guild",
    doc = "Opens the Hunter's Guild counter menu. valueA should be the player's client ID.",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_TALK_PL_V0_V2 = Opcode(
    code = 0x79,
    mnemonic = "npc_talk_pl",
    doc = "Creates an NPC which only appears near a given location. V0_V2 encodes the base register as a 32-bit integer.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "visibility radius", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
            Param(IntType, "initial state", "initial state (0 = alive, 1 = dead, 2 = invisible text box, according to qedit.info)", true, false),
            Param(IntType, "client ID", null, true, false),
        ), 4), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_TALK_PL_V3_V3_V4 = Opcode(
    code = 0x79,
    mnemonic = "npc_talk_pl_v3",
    doc = "Creates a V4 proximity controller. It constructs a local talk NPC in slot 3 while the local player is inside the radius and removes it outside the radius.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "visibility radius", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
            Param(IntType, "state", "State 1 installs the dead/incapacitated state object. State 2 is not established for V4.", true, false),
            Param(IntType, "controller token", "Selector used by npc_talk_kill; this is not the spawned NPC slot.", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_TALK_KILL = Opcode(
    code = 0x7A,
    mnemonic = "npc_talk_kill",
    doc = "On V4, deletes npc_talk_pl controllers matching the token. Multiple calls in one frame overwrite the global selector, so only the final token takes effect.",
    params = listOf(
        Param(IntType, "controller token", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_CRTPK = Opcode(
    code = 0x7B,
    mnemonic = "npc_crtpk",
    doc = "On V4, creates a networked attacker NPC with 6x69 command = 3. The examined handler uses NPC slot 1.",
    params = listOf(
        Param(IntType, "owner player slot", null, false, false),
        Param(IntType, "template index", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_CRPPK_V0_V2 = Opcode(
    code = 0x7C,
    mnemonic = "npc_crppk",
    doc = "Creates an attacker NPC. V0_V2 encodes the base register as a 32-bit integer and includes one ignored 32-bit argument.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 4), null, null, false, false),
        Param(IntType, "extra_arg", "Ignored 32-bit argument present in V0_V2 form but not V3_V4.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRPPK_V3_V3_V4 = Opcode(
    code = 0x7C,
    mnemonic = "npc_crppk_v3",
    doc = "Creates a networked attacker NPC at a position. V4 registers are x, y, z, angle, owner player slot, template index, and NPC slot.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "owner player slot", null, true, false),
            Param(IntType, "template index", null, true, false),
            Param(IntType, "NPC player slot", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRPTALK_V0_V2 = Opcode(
    code = 0x7D,
    mnemonic = "npc_crptalk",
    doc = "Creates an NPC with client ID 1. V0_V2 encodes the base register as a 32-bit integer and includes one ignored 32-bit argument.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "initial state", "initial state (0 = alive, 1 = dead, 2 = invisible text box, according to qedit.info)", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 4), null, null, false, false),
        Param(IntType, "extra_arg", "Ignored 32-bit argument present in V0_V2 form but not V3_V4.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRPTALK_V3_V3_V4 = Opcode(
    code = 0x7D,
    mnemonic = "npc_crptalk_v3",
    doc = "Creates a local talk NPC in slot 3 on V4. It does not send 6x69. State 1 installs the dead/incapacitated state object; state 2 has no confirmed special V4 handler branch.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "state", "State 1 installs the dead/incapacitated state object. State 2 is not established for V4.", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_P_LOOK_AT_V1 = Opcode(
    code = 0x7E,
    mnemonic = "p_look_at_v1",
    doc = "Causes client ID valueA to look at client ID valueB. Sends 6x3E.",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(IntType, "target client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_CRP_ID_V0_V2 = Opcode(
    code = 0x7F,
    mnemonic = "npc_crp_id",
    doc = "Creates an NPC. V0_V2 encodes the base register as a 32-bit integer and includes one ignored 32-bit argument.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "initial state", "initial state (0 = alive, 1 = dead, 2 = invisible text box, according to qedit.info)", true, false),
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 4), null, null, false, false),
        Param(IntType, "extra_arg", "Ignored 32-bit argument present in V0_V2 form but not V3_V4.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRP_ID_V3_V3_V4 = Opcode(
    code = 0x7F,
    mnemonic = "npc_crp_id_v3",
    doc = "Creates a networked follower NPC in an explicit player slot. V4 registers are x, y, z, angle, owner player slot, NPC slot, and template index.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "owner player slot", "Player slot that owns/controls the follower NPC.", true, false),
            Param(IntType, "NPC player slot", null, true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CAM_QUAKE = Opcode(
    code = 0x80,
    mnemonic = "cam_quake",
    doc = "Causes the camera to shake.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CAM_ADJ = Opcode(
    code = 0x81,
    mnemonic = "cam_adj",
    doc = "Moves the camera to where your character is looking.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CAM_ZMIN = Opcode(
    code = 0x82,
    mnemonic = "cam_zmin",
    doc = "Zooms the camera in or out.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CAM_ZMOUT = Opcode(
    code = 0x83,
    mnemonic = "cam_zmout",
    doc = "Zooms the camera in or out.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CAM_PAN_V0_V2 = Opcode(
    code = 0x84,
    mnemonic = "cam_pan",
    doc = "Pans the camera. V0_V2 inline form: 5 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:745)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "destination X", null, true, false),
            Param(IntType, "destination Y", null, true, false),
            Param(IntType, "destination Z", null, true, false),
            Param(IntType, "pan time", "pan time (in frames; 30 frames/sec)", true, false),
            Param(IntType, "end time", "end time (in frames; 30 frames/sec)", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V0_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_CAM_PAN_V3_V3_V4 = Opcode(
    code = 0x84,
    mnemonic = "cam_pan_v3",
    doc = "Pans the camera. (newserv QuestScript.cc:746)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "destination X", null, true, false),
            Param(IntType, "destination Y", null, true, false),
            Param(IntType, "destination Z", null, true, false),
            Param(IntType, "pan time", "pan time (in frames; 30 frames/sec)", true, false),
            Param(IntType, "end time", "end time (in frames; 30 frames/sec)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_GAME_LEV_SUPER = Opcode(
    code = 0x85,
    mnemonic = "game_lev_super",
    doc = "Temporarily sets the game's difficulty to Very Hard (even on v2). On v3 and later, does nothing.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GAME_LEV_RESET = Opcode(
    code = 0x86,
    mnemonic = "game_lev_reset",
    doc = "Restores the previous difficulty level after game_lev_super. On v3 and later, does nothing.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_POS_PIPE_V0_V2 = Opcode(
    code = 0x87,
    mnemonic = "pos_pipe",
    doc = "Create a telepipe at a specific position for the given player slot that takes players back to Pioneer 2 or the Lab. V0_V2 inline form: 4 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:759)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "owner client ID", "owner client ID (player or NPC must exist in the game)", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V0_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_POS_PIPE_V3_V3_V4 = Opcode(
    code = 0x87,
    mnemonic = "pos_pipe_v3",
    doc = "Create a telepipe at a specific position for the given player slot that takes players back to Pioneer 2 or the Lab. (newserv QuestScript.cc:760)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "owner client ID", "owner client ID (player or NPC must exist in the game)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_IF_ZONE_CLEAR = Opcode(
    code = 0x88,
    mnemonic = "if_zone_clear",
    doc = "Checks if all set events (enemies) have been destroyed in a given room",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "floor number", null, true, false),
            Param(IntType, "room ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CHK_ENE_NUM = Opcode(
    code = 0x89,
    mnemonic = "chk_ene_num",
    doc = "Retrieves the amount of enemies killed during the quest.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNHIDE_OBJ = Opcode(
    code = 0x8A,
    mnemonic = "unhide_obj",
    doc = "Constructs all objects or enemies that match the conditions",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "floor", null, true, false),
            Param(IntType, "room ID", null, true, false),
            Param(IntType, "group", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNHIDE_ENE = Opcode(
    code = 0x8B,
    mnemonic = "unhide_ene",
    doc = "Constructs all objects or enemies that match the conditions:",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AT_COORDS_CALL = Opcode(
    code = 0x8C,
    mnemonic = "at_coords_call",
    doc = "Starts a new thread when the player is close enough to the given point. The collision is created on the current floor; the thread is created when the player enters the given radius. After executing this opcode, regsA[0] is replaced with the object's token, which can be used in del_obj_param, move_coords_object, etc",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "radius", null, true, false),
            Param(ILabelType, "label index where thread should start", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AT_COORDS_TALK = Opcode(
    code = 0x8D,
    mnemonic = "at_coords_talk",
    doc = "Like at_coords_call, but the thread is not started automatically. Instead, the player's primary action button becomes \"talk\" within the radius, and the label is called when the player presses that button.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(ILabelType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_COL_NPCIN = Opcode(
    code = 0x8E,
    mnemonic = "col_npcin",
    doc = "Like at_coords_call, but only triggers if an NPC enters the radius.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
            Param(ILabelType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_COL_NPCINR = Opcode(
    code = 0x8F,
    mnemonic = "col_npcinr",
    doc = "Like at_coords_call, but triggers when a player within the event radius is also within the player radius of any other player",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "event radius", "event radius (centered at x, y, z defined above)", true, false),
            Param(IntType, "player radius", "player radius (centered at player)", true, false),
            Param(IntType, "label index where thread should start", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SWITCH_ON = Opcode(
    code = 0x90,
    mnemonic = "switch_on",
    doc = "Enables/disables a switch flag (valueA). Does NOT send 6x05, so other players will not know about this change! Use sw_send instead to keep switch flag state synced.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SWITCH_OFF = Opcode(
    code = 0x91,
    mnemonic = "switch_off",
    doc = "Enables/disables a switch flag (valueA). Does NOT send 6x05, so other players will not know about this change! Use sw_send instead to keep switch flag state synced.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PLAYBGM_EPI = Opcode(
    code = 0x92,
    mnemonic = "playbgm_epi",
    doc = "Plays a BGM. Values for valueA: 1: epi1.adx 2: epi2.adx 3: ED_PIANO.adx 4: matter.adx 5: open.adx 6: dreams.adx 7: mambo.adx 8: carnaval.adx 9: hearts.adx 10: smile.adx 11: nomal.adx 12: chu_f.adx 13: ENDING_LOOP.adx 14: DreamS_KIDS.adx 15: ESCAPE.adx 16: LIVE.adx 17: MILES.adx",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_MAINWARP = Opcode(
    code = 0x93,
    mnemonic = "set_mainwarp",
    doc = "Enables access to a floor (valueA) via the Pioneer 2 Ragol warp. Floors are 0-17 for Episode 1, 18-35 for Episode 2, 36-46 for Episode 4.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_OBJ_PARAM = Opcode(
    code = 0x94,
    mnemonic = "set_obj_param",
    doc = "Creates a targetable object.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "target radius", null, true, false),
            Param(ILabelType, "label number to call", null, true, false),
            Param(IntType, "distance from ground to target", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, false, true),
        ), 1), null, "Object handle.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_FLOOR_HANDLER_V0_V2 = Opcode(
    code = 0x95,
    mnemonic = "set_floor_handler",
    doc = "Causes the handler to be called on a new thread when the player warps to floorA. V0_V2 inline form: floor as 32-bit int, handler as 32-bit script offset. (newserv QuestScript.cc:828)",
    params = listOf(
        Param(IntType, "floor", "Floor number.", false, false),
        Param(IntType, "handler", "Handler function offset (32-bit, V0_V2 form).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_SET_FLOOR_HANDLER_V3_V4 = Opcode(
    code = 0x95,
    mnemonic = "set_floor_handler",
    doc = "Causes the labelB to be called on a new thread when the player warps to floorA. If the given floor already has a registered handler, it is replaced with the new one (each floor may have at most one handler). (newserv QuestScript.cc:829)",
    params = listOf(
        Param(IntType, "floor", "Floor number.", false, false),
        Param(ILabelType, null, "Handler function label.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_CLR_FLOOR_HANDLER = Opcode(
    code = 0x96,
    mnemonic = "clr_floor_handler",
    doc = "Deletes the handler for floorA.",
    params = listOf(
        Param(IntType, "floor", "Floor number.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_COL_PLINAW = Opcode(
    code = 0x97,
    mnemonic = "col_plinaw",
    doc = "Creates a collision object that checks if the NPC with client ID 1 is too far away from the player, when the player enters its check radius",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "check location X", null, true, false),
            Param(IntType, "check location Y", null, true, false),
            Param(IntType, "check location Z", null, true, false),
            Param(IntType, "check radius", null, true, false),
            Param(IntType, "label index for triggered function", null, true, false),
            Param(IntType, "player radius", "player radius (if NPC is closer, label is not called)", true, false),
            Param(IntType, "warp location X", null, true, false),
            Param(IntType, "warp location Y", null, true, false),
            Param(IntType, "warp location Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_HUD_HIDE = Opcode(
    code = 0x98,
    mnemonic = "hud_hide",
    doc = "Hides or shows the HUD.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_HUD_SHOW = Opcode(
    code = 0x99,
    mnemonic = "hud_show",
    doc = "Hides or shows the HUD.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CINE_ENABLE = Opcode(
    code = 0x9A,
    mnemonic = "cine_enable",
    doc = "Enables/disables the cinema effect (black bars above/below screen)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CINE_DISABLE = Opcode(
    code = 0x9B,
    mnemonic = "cine_disable",
    doc = "Enables/disables the cinema effect (black bars above/below screen)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NOP_A0_DEBUG = Opcode(
    code = 0xA0,
    mnemonic = "nop_a0_debug",
    doc = "Unused opcode. It's not clear what this was supposed to do. The behavior appears to be the same on all versions of PSO, from DC NTE through BB. argA is ignored. The game constructs a message list object from strB; the game will softlock unless this string contains exactly 2 messages (separated by \\n). After doing this, it destroys the message list and does nothing else.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_QT_FAILURE_V0_V2 = Opcode(
    code = 0xA1,
    mnemonic = "set_qt_failure",
    doc = "Sets a function to be called (on a new thread) when the quest is failed. V0_V2 inline form: 32-bit script offset. (newserv QuestScript.cc:860)",
    params = listOf(
        Param(IntType, "handler", "Handler function offset (32-bit, V0_V2 form).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_FAILURE_V3_V4 = Opcode(
    code = 0xA1,
    mnemonic = "set_qt_failure",
    doc = "Sets a function to be called (on a new thread) when the quest is failed. The quest is considered failed when you talk to the Hunter's Guild counter and r253 has the value 1 (specifically 1; other nonzero values do not trigger this function). (newserv QuestScript.cc:861)",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_SUCCESS_V0_V2 = Opcode(
    code = 0xA2,
    mnemonic = "set_qt_success",
    doc = "Like set_qt_failure, but uses r255 instead. V0_V2 inline form: 32-bit script offset. (newserv QuestScript.cc:865)",
    params = listOf(
        Param(IntType, "handler", "Handler function offset (32-bit, V0_V2 form).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_SUCCESS_V3_V4 = Opcode(
    code = 0xA2,
    mnemonic = "set_qt_success",
    doc = "Like set_qt_failure, but uses r255 instead. If r255 and r253 both have the value 1 when the player talks to the Hunter's Guild counter, the success label is called and the failure label is not called. (newserv QuestScript.cc:866)",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CLR_QT_FAILURE = Opcode(
    code = 0xA3,
    mnemonic = "clr_qt_failure",
    doc = "Clears the quest failure handler (opposite of set_qt_failure).",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CLR_QT_SUCCESS = Opcode(
    code = 0xA4,
    mnemonic = "clr_qt_success",
    doc = "Clears the quest success handler (opposite of set_qt_success).",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_QT_CANCEL_V0_V2 = Opcode(
    code = 0xA5,
    mnemonic = "set_qt_cancel",
    doc = "Sets a function to be called when the quest is cancelled via the Hunter's Guild counter. V0_V2 inline form: 32-bit script offset. (newserv QuestScript.cc:875)",
    params = listOf(
        Param(IntType, "handler", "Handler function offset (32-bit, V0_V2 form).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_CANCEL_V3_V4 = Opcode(
    code = 0xA5,
    mnemonic = "set_qt_cancel",
    doc = "Sets a function to be called when the quest is cancelled via the Hunter's Guild counter. (newserv QuestScript.cc:876)",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CLR_QT_CANCEL = Opcode(
    code = 0xA6,
    mnemonic = "clr_qt_cancel",
    doc = "Clears the quest cancel handler (opposite of set_qt_cancel).",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_PL_WALK_V0_V2 = Opcode(
    code = 0xA8,
    mnemonic = "pl_walk",
    doc = "Makes a player or NPC walk to a location. V0_V2 inline form: 4 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:884)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V0_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3F,
    argsMode = ArgsMode.Inline,
)
val OP_PL_WALK_V3_V3_V4 = Opcode(
    code = 0xA8,
    mnemonic = "pl_walk_v3",
    doc = "Makes a player or NPC walk to a location. (newserv QuestScript.cc:885)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_PL_ADD_MESETA = Opcode(
    code = 0xB0,
    mnemonic = "pl_add_meseta",
    doc = "Gives valueB Meseta to the player with client ID valueA. Negative values do not appear to be handled properly; if this opcode attempts to take more meseta than the player has, the player ends up with 999999 Meseta.",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(IntType, "amount", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_THREAD_STG = Opcode(
    code = 0xB1,
    mnemonic = "thread_stg",
    doc = "Starts a new thread at labelA in the quest script.",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DEL_OBJ_PARAM = Opcode(
    code = 0xB2,
    mnemonic = "del_obj_param",
    doc = "Deletes an interactable object previously created by set_obj_param. valueA is the object's token, as returned by regB from set_obj_param, or regsA[0] from e.g. at_coords_call.",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, true, false),
        ), 1), null, "Object handle.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_CREATE = Opcode(
    code = 0xB3,
    mnemonic = "item_create",
    doc = "Creates an item in the player's inventory. If the item is successfully created, this opcode sends 6x2B on all versions except BB. On BB, this opcode sends 6xCA, and the server sends 6xBE to create the item; the requested item must match one of the item creation masks in the quest script's header",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-2] [0]", null, true, false),
            Param(IntType, "item.data1[0-2] [1]", null, true, false),
            Param(IntType, "item.data1[0-2] [2]", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_CREATE2 = Opcode(
    code = 0xB4,
    mnemonic = "item_create2",
    doc = "Like item_create, but regsA specify all of item.data1 instead of only the first 3 bytes.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_DELETE = Opcode(
    code = 0xB5,
    mnemonic = "item_delete",
    doc = "Deletes an item from the player's inventory. Sends 6x29 if ths item is found and deleted. If the item is stackable, only one of it is deleted; the rest of the stack is not deleted. regsB must not wrap around (that is, the first register in regsB cannot be in the range [r245, r255]). If the item is not found, regsB[0] is set to 0xFFFFFFFF and the rest of regsB are not affected",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-11] for deleted item [0]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [1]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [2]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [3]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [4]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [5]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [6]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [7]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [8]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [9]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [10]", null, true, false),
            Param(IntType, "item.data1[0-11] for deleted item [11]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_DELETE2 = Opcode(
    code = 0xB6,
    mnemonic = "item_delete2",
    doc = "Deletes an item from the player's inventory.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-2] to search for [0]", null, true, false),
            Param(IntType, "item.data1[0-2] to search for [1]", null, true, false),
            Param(IntType, "item.data1[0-2] to search for [2]", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-11] for deleted item [0]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [1]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [2]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [3]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [4]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [5]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [6]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [7]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [8]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [9]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [10]", null, false, true),
            Param(IntType, "item.data1[0-11] for deleted item [11]", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_CHECK = Opcode(
    code = 0xB7,
    mnemonic = "item_check",
    doc = "Searches the player's inventory for an item and returns its item ID. The condition depends on the item's type: Weapons: data1[0-2] must match, and data1[4] (special/flags) must be 0 Armor/shield/unit: data1[0-2] must match Mag: data1[0-1] must match; regsA[2] is ignored Tool: data1[0-2] must match; if it's a tech disk, data1[4] must be 0",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-2] to search for, as above [0]", null, true, false),
            Param(IntType, "item.data1[0-2] to search for, as above [1]", null, true, false),
            Param(IntType, "item.data1[0-2] to search for, as above [2]", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SETEVT = Opcode(
    code = 0xB8,
    mnemonic = "setevt",
    doc = "Triggers set event valueA on the current floor. Sends 6x67.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_DIFFLVL = Opcode(
    code = 0xB9,
    mnemonic = "get_difflvl",
    doc = "Sets the given register to the current difficulty. 0 For normal, 1 for hard and 2 for both very hard and ultimate.\nUse get_difficulty_level2 if you want to differentiate between very hard and ultimate.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_EXIT_V1_V2 = Opcode(
    code = 0xBA,
    mnemonic = "set_qt_exit",
    doc = "Sets a label to be called (in a new thread) when the quest exits. V1_V2 inline form: 32-bit script offset. (newserv QuestScript.cc:942, F_V05_V2)",
    params = listOf(
        Param(IntType, "handler", "Handler function offset (32-bit, V1_V2 form).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QT_EXIT_V3_V4 = Opcode(
    code = 0xBA,
    mnemonic = "set_qt_exit",
    doc = "Sets a label to be called (in a new thread) when the quest exits. This happens during the unload procedure when leaving the game, so most opcodes cannot be used in these handlers. Generally they should only be used for setting quest flags, event flags, or quest counters. (newserv QuestScript.cc:943)",
    params = listOf(
        Param(ILabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CLR_QT_EXIT = Opcode(
    code = 0xBB,
    mnemonic = "clr_qt_exit",
    doc = "Clears the quest exit handler (opposite of set_qt_exit).",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NOP_BC = Opcode(
    code = 0xBC,
    mnemonic = "nop_bc",
    doc = "This opcode does nothing, even on 11/2000.",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PARTICLE_V1_V2 = Opcode(
    code = 0xC0,
    mnemonic = "particle",
    doc = "Creates a timed particle effect. V1_V2 inline form: 5 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:955, F_V05_V2)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "particle effect ID", null, true, false),
            Param(IntType, "duration", "duration (in frames; 30 frames/sec)", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V1_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_PARTICLE_V3_V3_V4 = Opcode(
    code = 0xC0,
    mnemonic = "particle_v3",
    doc = "Creates a timed particle effect. (newserv QuestScript.cc:956)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "effect type", null, true, false),
            Param(IntType, "duration", "duration (in frames; 30 frames/sec)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_TEXT = Opcode(
    code = 0xC1,
    mnemonic = "npc_text",
    doc = "Specifies what NPCs should say in various situations. This opcode sets strings for all NPCs; to set strings for only specific NPCs, use npc_text_id (on v3 and later). 00: NPC engaging in combat 01: NPC in danger (HP <= 75% if near player, <= 25% if far away?) 02: NPC casting any technique except those in cases 16 and 17 below 03: NPC has 20% or less TP 04: NPC died 05: NPC has been dead for a while 06: Unknown (possibly unused) 07: NPC lost sight of player 08: NPC locked on to an enemy 09: Player received a status effect 0A: Unknown (possibly unused) 0B: NPC standing still for 3 minutes 0C: Player in danger? (Like 01 but players reversed?) 0D: NPC completed 3-hit combo 0E: NPC hit for > 30% of max HP 0F: NPC hit for > 20% of max HP (and <= 30%) 10: NPC hit for > 1% of max HP (and <= 20%) 11: NPC healed by another player 12: Room cleared (wave cleared which did not trigger another wave) 13: NPC used a recovery item 14: NPC cannot heal / recover 15: Wave but not room cleared (wave triggered by another wave) 16: NPC casting Resta or Anti 17: NPC casting Foie, Zonde, or Barta 18: NPC regained sight of player (not valid on 11/2000)",
    params = listOf(
        Param(IntType, "situation number", "situation number:", false, false),
        Param(StringType, "string for NPC to say", "string for NPC to say (up to 52 characters)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_CHKWARP = Opcode(
    code = 0xC2,
    mnemonic = "npc_chkwarp",
    doc = "Warps an NPC to a predetermined location. See npc_check_straggle for more details.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_PL_PKOFF = Opcode(
    code = 0xC3,
    mnemonic = "pl_pkoff",
    doc = "Disables PK mode (battle mode) for a specific player. Sends 6x1C.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_MAP_DESIGNATE = Opcode(
    code = 0xC4,
    mnemonic = "map_designate",
    doc = "Specifies how objects and enemies should be populated for a floor. This legacy handler also uses the floor number as the map ID. On v2, map_designate_ex (F80D) added an explicit map-ID register.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "floor number", null, true, false),
            Param(IntType, "type", "type (0: use layout, 1: use offline template, 2: use online template, 3: nothing)", true, false),
            Param(IntType, "map variation", "Map/layout variation (object-set variation is set to zero).", true, false),
            Param(IntType, "object-set variation", "Used by type 2 on DC/PC. GC and BB force this value to zero.", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MASTERKEY_ON = Opcode(
    code = 0xC5,
    mnemonic = "masterkey_on",
    doc = "Locks all doors (masterkey_on) or removes that override (masterkey_off)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_MASTERKEY_OFF = Opcode(
    code = 0xC6,
    mnemonic = "masterkey_off",
    doc = "Locks all doors (masterkey_on) or removes that override (masterkey_off)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WINDOW_TIME = Opcode(
    code = 0xC7,
    mnemonic = "window_time",
    doc = "Enables/disables the timer window",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WINEND_TIME = Opcode(
    code = 0xC8,
    mnemonic = "winend_time",
    doc = "Enables/disables the timer window",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WINSET_TIME = Opcode(
    code = 0xC9,
    mnemonic = "winset_time",
    doc = "Sets the time displayed in the timer window. The value in regA should be a number of seconds.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GETMTIME = Opcode(
    code = 0xCA,
    mnemonic = "getmtime",
    doc = "Returns the time from the system clock. The value returned in regA depends on the client's architecture: - On DC, reads from hardware registers - On GC, reads from the TBRs - On PCv2, XB, and BB, calls QueryPerformanceCounter",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QUEST_BOARD_HANDLER_V1_V2 = Opcode(
    code = 0xCB,
    mnemonic = "set_quest_board_handler",
    doc = "Creates an item in the quest board. V1_V2 inline form: index as 32-bit int, handler as 32-bit script offset, name as C-string. (newserv QuestScript.cc:1026, F_V05_V2)",
    params = listOf(
        Param(IntType, "index of item", "index of item (0-5)", false, false),
        Param(IntType, "handler", "Handler label offset (32-bit, V1_V2 form).", false, false),
        Param(StringType, "name of item in the Quest Board window", null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_SET_QUEST_BOARD_HANDLER_V3_V4 = Opcode(
    code = 0xCB,
    mnemonic = "set_quest_board_handler",
    doc = "Creates an item in the quest board. Executing this opcode is not enough for the item to appear! The item only appears if its corresponding display register is also set to 1. The display registers are r74-r79 (for QB indexes 0-5, respectively). (newserv QuestScript.cc:1027)",
    params = listOf(
        Param(IntType, "index of item", "index of item (0-5)", false, false),
        Param(ILabelType, "label to call when selected", null, false, false),
        Param(StringType, "name of item in the Quest Board window", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_CLEAR_QUEST_BOARD_HANDLER = Opcode(
    code = 0xCC,
    mnemonic = "clear_quest_board_handler",
    doc = "Deletes an item by index from the quest board.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PARTICLE_ID_V1_V2 = Opcode(
    code = 0xCD,
    mnemonic = "particle_id",
    doc = "Creates a particle effect on a given entity. V1_V2 inline form: 4 registers (4-byte encoding) + one extra 32-bit argument. (newserv QuestScript.cc:1038, F_V05_V2)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "particle effect ID", null, true, false),
            Param(IntType, "duration in frames", null, true, false),
            Param(IntType, "entity", "entity (client ID, 0x1000 + enemy ID, or 0x4000 + object ID)", true, false),
            Param(IntType, "y offset", "y offset (as integer)", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "extra_arg", "Extra 32-bit argument present in V1_V2 form but not V3_V4. (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_PARTICLE_ID_V3_V3_V4 = Opcode(
    code = 0xCD,
    mnemonic = "particle_id_v3",
    doc = "Creates a particle effect on a given entity. (newserv QuestScript.cc:1039)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "effect type", null, true, false),
            Param(IntType, "duration in frames", null, true, false),
            Param(IntType, "entity", "entity (client ID, 0x1000 + enemy ID, or 0x4000 + object ID)", true, false),
            Param(IntType, "y offset", "y offset (as integer)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRPTALK_ID_V1_V2 = Opcode(
    code = 0xCE,
    mnemonic = "npc_crptalk_id",
    doc = "Creates an NPC. V1_V2 encodes the base register as a 32-bit integer and includes one ignored 32-bit argument.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "initial state", "initial state (0 = alive, 1 = dead, 2 = invisible text box, according to qedit.info)", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
            Param(IntType, "client ID", null, true, false),
        ), 4), null, null, false, false),
        Param(IntType, "extra_arg", "Ignored 32-bit argument present in V1_V2 form but not V3_V4.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CRPTALK_ID_V3_V3_V4 = Opcode(
    code = 0xCE,
    mnemonic = "npc_crptalk_id_v3",
    doc = "Creates a local talk NPC in an explicit player slot on V4. It does not send 6x69.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "position X", null, true, false),
            Param(IntType, "position Y", null, true, false),
            Param(IntType, "position Z", null, true, false),
            Param(IntType, "angle", null, true, false),
            Param(IntType, "state", "State 1 installs the dead/incapacitated state object. State 2 is not established for V4.", true, false),
            Param(IntType, "template index", "template index (see 6x69 in CommandFormats.hh)", true, false),
            Param(IntType, "NPC player slot", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_LANG_CLEAN = Opcode(
    code = 0xCF,
    mnemonic = "npc_lang_clean",
    doc = "Deletes all strings registered with npc_text.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_PL_PKON = Opcode(
    code = 0xD0,
    mnemonic = "pl_pkon",
    doc = "Enables PK mode (battle mode) for a specific player. Sends 6x1B.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_PL_CHK_ITEM2 = Opcode(
    code = 0xD1,
    mnemonic = "pl_chk_item2",
    doc = "Like find_inventory_item, but regsA specifies data1[0-2] as well as data1[4]. The matching conditions are the same as in find_inventory_item except that data1[4] must match regsA[3], instead of zero. Returns the item ID in regB, or FFFFFFFF if not found.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ENABLE_MAINMENU = Opcode(
    code = 0xD2,
    mnemonic = "enable_mainmenu",
    doc = "Enables/disables the main menu and shortcut menu.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISABLE_MAINMENU = Opcode(
    code = 0xD3,
    mnemonic = "disable_mainmenu",
    doc = "Enables/disables the main menu and shortcut menu.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_START_BATTLEBGM = Opcode(
    code = 0xD4,
    mnemonic = "start_battlebgm",
    doc = "Enables or disables battle music override. When enabled, the battle segments of the BGM will play regardless of whether there are enemies nearby. Changing this value only takes effect after any currently-queued music segments are done playing. The override is cleared upon changing areas.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_END_BATTLEBGM = Opcode(
    code = 0xD5,
    mnemonic = "end_battlebgm",
    doc = "Enables or disables battle music override. When enabled, the battle segments of the BGM will play regardless of whether there are enemies nearby. Changing this value only takes effect after any currently-queued music segments are done playing. The override is cleared upon changing areas.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISP_MSG_QB = Opcode(
    code = 0xD6,
    mnemonic = "disp_msg_qb",
    doc = "Shows a message in the Quest Board message window",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CLOSE_MSG_QB = Opcode(
    code = 0xD7,
    mnemonic = "close_msg_qb",
    doc = "Closes the Quest Board message window",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_EVENTFLAG_V3 = Opcode(
    code = 0xD8,
    mnemonic = "set_eventflag_v3",
    doc = "Writes the valueB (a single byte) to the event flag specified by valueA.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SYNC_REGISTER_V1_V2 = Opcode(
    code = 0xD9,
    mnemonic = "sync_register",
    doc = "Sets regA to valueB, and sends 6x77 so other clients will also set their local regA to valueB. V1_V2 stack form: register number is 4 bytes in actual binary (psolib models as 1 byte). (newserv QuestScript.cc:1082)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Stack,
)
val OP_SYNC_REGISTER_V3_V4 = Opcode(
    code = 0xD9,
    mnemonic = "sync_register",
    doc = "Sets regA to valueB, and sends 6x77 so other clients will also set their local regA to valueB. (newserv QuestScript.cc:1083)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_SET_RETURNHUNTER = Opcode(
    code = 0xDA,
    mnemonic = "set_returnhunter",
    doc = "TODO: Document these",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_RETURNCITY = Opcode(
    code = 0xDB,
    mnemonic = "set_returncity",
    doc = null,
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LOAD_PVR = Opcode(
    code = 0xDC,
    mnemonic = "load_pvr",
    doc = "TODO: Document this",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LOAD_MIDI = Opcode(
    code = 0xDD,
    mnemonic = "load_midi",
    doc = "TODO: Document this Does nothing on all non-DC versions.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_UNKNOWN_DE = Opcode(
    code = 0xDE,
    mnemonic = "unknown_de",
    doc = "Finds an item in the player's bank, and clears its entry in the bank",
    params = listOf(
        Param(RegType(listOf(
            Param(AnyType.Instance, "item.data1[0-5] [0]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
            Param(AnyType.Instance, "item.data1[0-5] [1]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
            Param(AnyType.Instance, "item.data1[0-5] [2]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
            Param(AnyType.Instance, "item.data1[0-5] [3]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
            Param(AnyType.Instance, "item.data1[0-5] [4]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
            Param(AnyType.Instance, "item.data1[0-5] [5]", "item.data1[0-5] (bank item must exactly match all bytes)", true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(AnyType.Instance, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_PARAM_V1_V2 = Opcode(
    code = 0xDF,
    mnemonic = "npc_param",
    doc = "Sets NPC AI behaviors. V1_V2 inline form: 14 registers (4-byte encoding) + one 32-bit int argument. (newserv QuestScript.cc:1130)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, "base level. NPC's actual level depends on difficulty", "base level. NPC's actual level depends on difficulty:", true, false),
            Param(IntType, "sets value of technique_flags in the TNpcPlayer object", "sets value of technique_flags in the TNpcPlayer object:", true, false),
            Param(IntType, "enemy lock-on range", null, true, false),
            Param(IntType, "unknown", "unknown (TODO)", true, false),
            Param(IntType, "max distance from player", null, true, false),
            Param(IntType, "enemy unlock range", null, true, false),
            Param(IntType, "block range", null, true, false),
            Param(IntType, "attack range", "attack range (not necessarily equal to weapon range)", true, false),
            Param(IntType, "attack technique level", "attack technique level (which techniques is specified by technique_flags)", true, false),
            Param(IntType, "support technique level", "support technique level (Resta/Anti)", true, false),
            Param(IntType, "attack probability", "attack probability (in range [0, 100])", true, false),
            Param(IntType, "attack technique probability", "attack technique probability (in range [0, 100])", true, false),
            Param(IntType, "unknown", "unknown (TODO: see TNpcPlayer_FUN_801514c0); appears to be a distance range, and applies only for", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "NPC template to modify", "NPC template to modify (00-3F). (Note: register number is 4 bytes in actual binary, but psolib models as 1 byte.)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_PARAM_V3_V3_V4 = Opcode(
    code = 0xDF,
    mnemonic = "npc_param_v3",
    doc = "Sets NPC AI behaviors. Normal: min(base level + 1, 199) Hard: min(base level + 26, 199) Very Hard: min(base level + 51, 199) Ultimate: min(base level + 151, 199) 00 = technique_flags=40 (no techniques) 01 = technique_flags=04 (has Resta, Anti, Foie, and Gifoie) 02 = technique_flags=08 (has Resta, Anti, Barta, and Gibarta) 03, 0A = technique_flags=C0 (no techniques, doesn't back off (see regsA[13])) 0B = technique_flags=84 (has Resta, Anti, Foie, and Gifoie, doesn't back off (see regsA[13])) 0C = technique_flags=88 (has Resta, Anti, Barta, and Gibarta, doesn't back off (see regsA[13])) 0D = technique_flags=90 (has Resta, Anti, Zonde, and Razonde, doesn't back off (see regsA[13])) Anything else = uninitialized value written to technique_flags NPCs that attack the player and do not have technique_flags & 0x80; could be backoff distance when attacked by a player. (newserv QuestScript.cc:1131)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, "base level. NPC's actual level depends on difficulty", "base level. NPC's actual level depends on difficulty:", true, false),
            Param(IntType, "sets value of technique_flags in the TNpcPlayer object", "sets value of technique_flags in the TNpcPlayer object:", true, false),
            Param(IntType, "enemy lock-on range", null, true, false),
            Param(IntType, "unknown", "unknown (TODO)", true, false),
            Param(IntType, "max distance from player", null, true, false),
            Param(IntType, "enemy unlock range", null, true, false),
            Param(IntType, "block range", null, true, false),
            Param(IntType, "attack range", "attack range (not necessarily equal to weapon range)", true, false),
            Param(IntType, "attack technique level", "attack technique level (which techniques is specified by technique_flags)", true, false),
            Param(IntType, "support technique level", "support technique level (Resta/Anti)", true, false),
            Param(IntType, "attack probability", "attack probability (in range [0, 100])", true, false),
            Param(IntType, "attack technique probability", "attack technique probability (in range [0, 100])", true, false),
            Param(IntType, "unknown", "unknown (TODO: see TNpcPlayer_FUN_801514c0); appears to be a distance range, and applies only for", true, false),
        ), 1), null, null, false, false),
        Param(IntType, "NPC template to modify", "NPC template to modify (00-3F)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_PAD_DRAGON = Opcode(
    code = 0xE0,
    mnemonic = "pad_dragon",
    doc = "TODO(DX): Document this. It enables a flag that affects some logic in TBoss1Dragon::update. The flag is disabled when the Dragon's boss arena unloads, but not when it loads, so it can be set when the player is in a different area. It appears the flag is not cleared if the player never enters the Dragon arena, so it seems the only advisable place to use this would be immediately after the player enters the Dragon arena.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CLEAR_MAINWARP = Opcode(
    code = 0xE1,
    mnemonic = "clear_mainwarp",
    doc = "Disables access to a floor (valueA) via the Pioneer 2 Ragol warp. This is the logical opposite of set_mainwarp.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PCAM_PARAM_V1_V2 = Opcode(
    code = 0xE2,
    mnemonic = "pcam_param",
    doc = "Sets camera parameters for the current frame. V1_V2 inline form: 6 registers (4-byte encoding). (newserv QuestScript.cc:1145)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "relative location of focus point from player [0]", null, true, false),
            Param(IntType, "relative location of focus point from player [1]", null, true, false),
            Param(IntType, "relative location of focus point from player [2]", null, true, false),
            Param(IntType, "relative location of camera from player [0]", null, true, false),
            Param(IntType, "relative location of camera from player [1]", null, true, false),
            Param(IntType, "relative location of camera from player [2]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_PCAM_PARAM_V3_V3_V4 = Opcode(
    code = 0xE2,
    mnemonic = "pcam_param_v3",
    doc = "Sets camera parameters for the current frame. (newserv QuestScript.cc:1146)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "relative location of focus point from player [0]", null, true, false),
            Param(IntType, "relative location of focus point from player [1]", null, true, false),
            Param(IntType, "relative location of focus point from player [2]", null, true, false),
            Param(IntType, "relative location of camera from player [0]", null, true, false),
            Param(IntType, "relative location of camera from player [1]", null, true, false),
            Param(IntType, "relative location of camera from player [2]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_START_SETEVT = Opcode(
    code = 0xE3,
    mnemonic = "start_setevt",
    doc = "Triggers set event (valueB) on floor (valueA). Sends 6x67.",
    params = listOf(
        Param(IntType, "floor", null, false, false),
        Param(IntType, "event ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_WARP_ON = Opcode(
    code = 0xE4,
    mnemonic = "warp_on",
    doc = "Enables or disables warps",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WARP_OFF = Opcode(
    code = 0xE5,
    mnemonic = "warp_off",
    doc = "Enables or disables warps",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_SLOTNUMBER = Opcode(
    code = 0xE6,
    mnemonic = "get_slotnumber",
    doc = "Returns the client ID of the local client",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_SERVERNUMBER = Opcode(
    code = 0xE7,
    mnemonic = "get_servernumber",
    doc = "Returns the index of the player who is the leader of the party.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_EVENTFLAG2 = Opcode(
    code = 0xE8,
    mnemonic = "set_eventflag2",
    doc = "Sets an event flag from a register. In v3 and later, this is not needed, since set_eventflag can be called with F_ARGS, but it still exists.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_RES = Opcode(
    code = 0xE9,
    mnemonic = "res",
    doc = "regA %= regB This is exactly the same as the mod opcode (including its quirk).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MODI2_V1_V4 = Opcode(
    code = 0xEA,
    mnemonic = "modi2",
    doc = "modify register (newserv QuestScript.cc:1172, F_V1_V4) regA %= valueB This is exactly the same as the modi opcode (including its quirk).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFE,
    argsMode = ArgsMode.Inline,
)
val OP_ENABLE_BGMCTRL = Opcode(
    code = 0xEB,
    mnemonic = "enable_bgmctrl",
    doc = "Changes the background music. create_bgmctrl must be run before doing this. The values for valueA are the same as for playbgm_epi.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SW_SEND = Opcode(
    code = 0xEC,
    mnemonic = "sw_send",
    doc = "Changes the state of a switch flag and sends the update to all players (unlike switch_on/switch_off)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "switch flag number", null, true, false),
            Param(IntType, "floor number", null, true, false),
            Param(IntType, "flags", "flags (see 6x05 definition in CommandFormats.hh)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CREATE_BGMCTRL = Opcode(
    code = 0xED,
    mnemonic = "create_bgmctrl",
    doc = "Creates a BGM controller object. Use this before set_bgm.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_PL_ADD_MESETA2 = Opcode(
    code = 0xEE,
    mnemonic = "pl_add_meseta2",
    doc = "Like pl_add_meseta, but can only give Meseta to the local player.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SYNC_REGISTER2_V1_V2 = Opcode(
    code = 0xEF,
    mnemonic = "sync_register2",
    doc = "Sets regA to regB's value, and sends 6x77. V1_V2 inline form: two 4-byte register numbers (W_REG32 + R_REG32 encoding, confirmed in newserv for DC_V2 and PC_V2). Modeled as int+int to correctly consume 8 arg bytes. (newserv QuestScript.cc:1192)",
    params = listOf(
        Param(IntType, "dst_reg", "Destination register number (4-byte little-endian encoding).", false, false),
        Param(IntType, "src_reg", "Source register number (4-byte little-endian encoding).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3E,
    argsMode = ArgsMode.Inline,
)
val OP_SYNC_REGISTER2_V3_V4 = Opcode(
    code = 0xEF,
    mnemonic = "sync_register2",
    doc = "Like sync_register, but takes the value from another register rather than using an immediate value. On v3 and later, this is identical to sync_register. (newserv QuestScript.cc:1193)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_SEND_REGWORK = Opcode(
    code = 0xF0,
    mnemonic = "send_regwork",
    doc = "Same as sync_register2, but sends the value via UDP if UDP is enabled. This opcode was removed after GC NTE and is missing from v3 and v4.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LETI_FIXED_CAMERA_V2 = Opcode(
    code = 0xF1,
    mnemonic = "leti_fixed_camera",
    doc = "Sets the camera's location and angle. V2 inline form: 6 registers (4-byte encoding). (newserv QuestScript.cc:1202)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "camera location X", null, true, false),
            Param(IntType, "camera location Y", null, true, false),
            Param(IntType, "camera location Z", null, true, false),
            Param(IntType, "camera focus location X", null, true, false),
            Param(IntType, "camera focus location Y", null, true, false),
            Param(IntType, "camera focus location Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_LETI_FIXED_CAMERA_V3_V3_V4 = Opcode(
    code = 0xF1,
    mnemonic = "leti_fixed_camera_v3",
    doc = "Sets the camera's location and angle. (newserv QuestScript.cc:1203)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "camera location X", null, true, false),
            Param(IntType, "camera location Y", null, true, false),
            Param(IntType, "camera location Z", null, true, false),
            Param(IntType, "camera focus location X", null, true, false),
            Param(IntType, "camera focus location Y", null, true, false),
            Param(IntType, "camera focus location Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_DEFAULT_CAMERA_POS1 = Opcode(
    code = 0xF2,
    mnemonic = "default_camera_pos1",
    doc = "Resets the camera to non-fixed (default behavior).",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_GC_NUMBER = Opcode(
    code = 0xFA,
    mnemonic = "get_gc_number",
    doc = null,
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNKNOWN_FB = Opcode(
    code = 0xFB,
    mnemonic = "unknown_fb",
    doc = null,
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DEBUG_F800 = Opcode(
    code = 0xF800,
    mnemonic = "debug_f800",
    doc = "Same as 50, but uses fixed arguments - with the string \"俺は節政だ！！\", which Google Translate translates as \"I am frugal!!\"",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_CHAT_CALLBACK = Opcode(
    code = 0xF801,
    mnemonic = "set_chat_callback",
    doc = "Creates a region that calls a label if a specified string is said (via chat) by a player within the region. This is implemented by the TOChatSensor object; see that object's comments on Map.cc for details. If the trigger string contains any newlines (\\n), it is truncated at the first newline. Before matching, the game prepends and appends a single space to the chat message (presumably so that callers can use strings like \" word \" to avoid matching substrings of longer words) and transforms the chat message to lowercase, so strB should not contain any uppercase letters. The lowercasing logic (probably accidentally) also affects some symbols, as follows: [ => { \\ => | ] => } ^ => ~ _ => <DEL> Curiously, the function that checks for matches appears to be used incorrectly. The function takes an array of char[0x20] and returns the index of the string that matched, or -1 if none matched. This list of match strings is expected to end with an empty string, but TOChatSensor doesn't correctly terminate it this way. However, the following field is the TQuestThread pointer, which is non-null only if the sensor has already triggered, so it doesn't misbehave",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "radius", null, true, false),
            Param(IntType, "label index", null, true, false),
        ), 1), null, null, false, false),
        Param(StringType, "trigger string", "trigger string (up to 31 characters)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_DIFFICULTY_LEVEL2 = Opcode(
    code = 0xF808,
    mnemonic = "get_difficulty_level2",
    doc = "Sets the given register to the current difficulty. 0 For normal, 1 for hard, 2 for very hard and 3 for ultimate.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_NUMBER_OF_PLAYER1 = Opcode(
    code = 0xF809,
    mnemonic = "get_number_of_player1",
    doc = "Set the given register to the current number of players. Either 1, 2, 3 or 4.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_COORD_OF_PLAYER = Opcode(
    code = 0xF80A,
    mnemonic = "get_coord_of_player",
    doc = "Retrieves a player's position.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "returned location X", null, false, true),
            Param(IntType, "returned location Y", null, false, true),
            Param(IntType, "returned location Z", null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ENABLE_MAP = Opcode(
    code = 0xF80B,
    mnemonic = "enable_map",
    doc = "Enables or disables the area map and minimap.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISABLE_MAP = Opcode(
    code = 0xF80C,
    mnemonic = "disable_map",
    doc = "Enables or disables the area map and minimap.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_MAP_DESIGNATE_EX = Opcode(
    code = 0xF80D,
    mnemonic = "map_designate_ex",
    doc = "Like map_designate, but also allows changing the client map ID and object-set variation (the client's objset field).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "floor number", null, true, false),
            Param(IntType, "map ID", null, true, false),
            Param(IntType, "type", "type (0: use layout, 1: use offline template, 2: use online template, 3: nothing)", true, false),
            Param(IntType, "map variation", null, true, false),
            Param(IntType, "object-set variation", "The client's objset field; used by type 2.", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISABLE_WEAPON_DROP = Opcode(
    code = 0xF80E,
    mnemonic = "disable_weapon_drop",
    doc = "Enables or disables weapon dropping upon death for a player. Sends 6x81 (disable) or 6x82 (enable).",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ENABLE_WEAPON_DROP = Opcode(
    code = 0xF80F,
    mnemonic = "enable_weapon_drop",
    doc = "Enables or disables weapon dropping upon death for a player. Sends 6x81 (disable) or 6x82 (enable).",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_INITIAL_FLOOR = Opcode(
    code = 0xF810,
    mnemonic = "ba_initial_floor",
    doc = "Sets the floor where the players will start. This generally should be used in the start label (where map_designate, etc. are used). valueA specifies which floor to start on, but indirectly: valueA > 1 and < 0x12: Episode 1 areas (Pioneer 2, Forest 1, etc.) valueA >= 0x12: No effect Note that if the player doesn't start in the city (Pioneer 2 or Lab), the client will not reload the common and rare item tables (ItemPT and ItemRT). This means, for example, that starting an Episode 2 quest from an Episode 1 game will result in Episode 2 enemies' drop-anything rates being all zeroes, since the Episode 1 ItemPT is still loaded. This is only an issue in client drop mode; on newserv, you can forbid client drop mode in the quest's metadata JSON file if needed. (See q058.json for documentation on how to do this.)",
    params = listOf(
        Param(IntType, "0: Temple", "0: Temple (floor 0x10)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_BA_RULES = Opcode(
    code = 0xF811,
    mnemonic = "set_ba_rules",
    doc = "Clears all battle rules.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BA_SET_TECH = Opcode(
    code = 0xF812,
    mnemonic = "ba_set_tech",
    doc = "Sets the tech disk mode in battle. valueA (does NOT match enum): 0 => FORBID_ALL 1 => ALLOW 2 => LIMIT_LEVEL",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_EQUIP = Opcode(
    code = 0xF813,
    mnemonic = "ba_set_equip",
    doc = "Sets the weapon and armor mode in battle. valueA (does NOT match enum): 0 => FORBID_ALL 1 => ALLOW 2 => CLEAR_AND_ALLOW 3 => FORBID_RARES",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_MAG = Opcode(
    code = 0xF814,
    mnemonic = "ba_set_mag",
    doc = "Sets the mag mode in battle. valueA (does NOT match enum): 0 => FORBID_ALL 1 => ALLOW",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_ITEM = Opcode(
    code = 0xF815,
    mnemonic = "ba_set_item",
    doc = "Sets the tool mode in battle. valueA (does NOT match enum): 0 => FORBID_ALL 1 => ALLOW 2 => CLEAR_AND_ALLOW",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_TRAPMENU = Opcode(
    code = 0xF816,
    mnemonic = "ba_set_trapmenu",
    doc = "Sets the trap mode in battle. valueA (matches enum): 0 => DEFAULT 1 => ALL_PLAYERS",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_UNUSED_F817 = Opcode(
    code = 0xF817,
    mnemonic = "ba_set_unused_f817",
    doc = "This appears to be unused - the value is copied into the main battle rules struct, but the field is never read from there. This may have been an early implementation of F851 that affected all trap types, but this field is no longer used.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_RESPAWN = Opcode(
    code = 0xF818,
    mnemonic = "ba_set_respawn",
    doc = "Sets the respawn mode in battle. valueA (does NOT match enum): 0 => FORBID 1 => ALLOW 2 => LIMIT_LIVES",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_CHAR = Opcode(
    code = 0xF819,
    mnemonic = "ba_set_char",
    doc = "Enables (1) or disables (0) character replacement in battle mode",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_DROPWEP = Opcode(
    code = 0xF81A,
    mnemonic = "ba_dropwep",
    doc = "Enables (1) or disables (0) weapon dropping upon death in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_TEAMS = Opcode(
    code = 0xF81B,
    mnemonic = "ba_teams",
    doc = "Enables (1) or disables (0) teams in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_DISP_MSG = Opcode(
    code = 0xF81C,
    mnemonic = "ba_disp_msg",
    doc = "Shows the rules window and starts the battle. This should be used after setting up all the rules with the various ba_* opcodes.",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DEATH_LVL_UP = Opcode(
    code = 0xF81D,
    mnemonic = "death_lvl_up",
    doc = "Sets the number of levels to gain upon respawn in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_MESETA = Opcode(
    code = 0xF81E,
    mnemonic = "ba_set_meseta",
    doc = "Sets the Meseta mode in battle. valueA (matches enum): 0 => ALLOW 1 => FORBID_ALL 2 => CLEAR_AND_ALLOW",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CMODE_STAGE = Opcode(
    code = 0xF820,
    mnemonic = "cmode_stage",
    doc = "Sets the challenge mode stage number",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NOP_F821 = Opcode(
    code = 0xF821,
    mnemonic = "nop_f821",
    doc = "regsA[3-8] specify first 6 bytes of an ItemData. This opcode consumes an item ID, but does nothing else.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NOP_F822 = Opcode(
    code = 0xF822,
    mnemonic = "nop_f822",
    doc = "This opcode does nothing. It has two branches (one for online, one for offline), but both branches do nothing.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_CMODE_CHAR_TEMPLATE = Opcode(
    code = 0xF823,
    mnemonic = "set_cmode_char_template",
    doc = "Set character template for challenge mode",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_CMODE_DIFF = Opcode(
    code = 0xF824,
    mnemonic = "set_cmode_diff",
    doc = "Set challenge mode difficulty: 0=normal, 1=hard, 2=very hard, 3=ultimate",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_EXP_MULTIPLICATION = Opcode(
    code = 0xF825,
    mnemonic = "exp_multiplication",
    doc = "Sets the factor by which all EXP is multiplied in challenge mode. The multiplier value is regsA[0] + (regsA[1] / regsA[2]).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_EXP_DIVISION = Opcode(
    code = 0xF826,
    mnemonic = "exp_division",
    doc = "Checks if any player is still alive in challenge mode. Returns 1 if all players are dead, or 0 if not.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_USER_IS_DEAD = Opcode(
    code = 0xF827,
    mnemonic = "get_user_is_dead",
    doc = "Checks if all players are still alive in challenge mode. Returns 1 if any player is dead, or 0 if not.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GO_FLOOR = Opcode(
    code = 0xF828,
    mnemonic = "go_floor",
    doc = "Sends a player to the given floor.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_NUM_KILLS = Opcode(
    code = 0xF829,
    mnemonic = "get_num_kills",
    doc = "Returns the number of enemies a player has killed during the quest.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_RESET_KILLS = Opcode(
    code = 0xF82A,
    mnemonic = "reset_kills",
    doc = "Resets the kill count for the player specified by regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNLOCK_DOOR2 = Opcode(
    code = 0xF82B,
    mnemonic = "unlock_door2",
    doc = "Sets or clears a switch flag, and synchronizes the value to all players",
    params = listOf(
        Param(IntType, "floor", null, false, false),
        Param(IntType, "switch flag index", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_LOCK_DOOR2 = Opcode(
    code = 0xF82C,
    mnemonic = "lock_door2",
    doc = "Sets or clears a switch flag, and synchronizes the value to all players.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_IF_SWITCH_NOT_PRESSED = Opcode(
    code = 0xF82D,
    mnemonic = "if_switch_not_pressed",
    doc = "Checks a switch flag on the current floor",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "switch flag index", null, true, false),
            Param(IntType, "result", "result (0 or 1)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_IF_SWITCH_PRESSED = Opcode(
    code = 0xF82E,
    mnemonic = "if_switch_pressed",
    doc = "Returns 1 if the switch is pressed, 0 otherwise.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "floor", null, true, false),
            Param(IntType, "switch flag index", null, true, false),
            Param(IntType, "result", "result (0 or 1)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNKNOWN_F82F = Opcode(
    code = 0xF82F,
    mnemonic = "unknown_f82f",
    doc = null,
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CONTROL_DRAGON = Opcode(
    code = 0xF830,
    mnemonic = "control_dragon",
    doc = "Enables a player to control the Dragon. valueA specifies the client ID.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_RELEASE_DRAGON = Opcode(
    code = 0xF831,
    mnemonic = "release_dragon",
    doc = "Disables player control of the Dragon.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SHRINK = Opcode(
    code = 0xF838,
    mnemonic = "shrink",
    doc = "Shrinks a player or returns them to normal size. regA specifies the client ID.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNSHRINK = Opcode(
    code = 0xF839,
    mnemonic = "unshrink",
    doc = "Shrinks a player or returns them to normal size. regA specifies the client ID.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SHRINK_CAM1 = Opcode(
    code = 0xF83A,
    mnemonic = "set_shrink_cam1",
    doc = "These set some camera parameters for the specified player. These parameters appear to be unused, so these opcodes essentially do nothing",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "a Vector3F X", null, true, false),
            Param(IntType, "a Vector3F Y", null, true, false),
            Param(IntType, "a Vector3F Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SHRINK_CAM2 = Opcode(
    code = 0xF83B,
    mnemonic = "set_shrink_cam2",
    doc = "These set some camera parameters for the specified player. These parameters appear to be unused, so these opcodes essentially do nothing.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISPLAY_CLOCK2 = Opcode(
    code = 0xF83C,
    mnemonic = "display_clock2",
    doc = "Shows the timer window in challenge mode. regA is the time value to display, in seconds.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_AREA_TOTAL = Opcode(
    code = 0xF83D,
    mnemonic = "set_area_total",
    doc = "Sets the total number of areas across all challenge quests for the current episode.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DELETE_AREA_TITLE = Opcode(
    code = 0xF83E,
    mnemonic = "delete_area_title",
    doc = "Sets the number of the current challenge mode area.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_LOAD_NPC_DATA = Opcode(
    code = 0xF840,
    mnemonic = "load_npc_data",
    doc = "On V4, enables one-shot use of the staged custom NPC character data. The next applicable NPC creation clears this flag. It may run before or after get_npc_data, but both must complete before the creation.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_NPC_DATA = Opcode(
    code = 0xF841,
    mnemonic = "get_npc_data",
    doc = "On V4, copies 0x70 bytes from the data label into the staged custom NPC character-data buffer. It may run before or after load_npc_data, but both must complete before the consuming creation.",
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GIVE_DAMAGE_SCORE = Opcode(
    code = 0xF848,
    mnemonic = "give_damage_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_TAKE_DAMAGE_SCORE = Opcode(
    code = 0xF849,
    mnemonic = "take_damage_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNK_SCORE_F84A = Opcode(
    code = 0xF84A,
    mnemonic = "unk_score_f84a",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNK_SCORE_F84B = Opcode(
    code = 0xF84B,
    mnemonic = "unk_score_f84b",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_KILL_SCORE = Opcode(
    code = 0xF84C,
    mnemonic = "kill_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DEATH_SCORE = Opcode(
    code = 0xF84D,
    mnemonic = "death_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_UNK_SCORE_F84E = Opcode(
    code = 0xF84E,
    mnemonic = "unk_score_f84e",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ENEMY_DEATH_SCORE = Opcode(
    code = 0xF84F,
    mnemonic = "enemy_death_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MESETA_SCORE = Opcode(
    code = 0xF850,
    mnemonic = "meseta_score",
    doc = "These are used to set the scores for each type of action in battle mode. The value used in each of these are regsA[0] + (regsA[1] / regsA[2]), treated as a floating-point value. For example, one way to specify the value 3.4 would be to set regsA to {3, 2, 5}. These values are not reset between battle quests or upon joining/leaving games, so battle quests should always explicitly set these! The scores which can be set are: ba_player_give_damage_score: Score earned per point of damage given to other players. (Default 0.05) ba_player_take_damage_score: Score lost per point of damage taken from other players. (Default 0.02) ba_enemy_give_damage_score: Score earned per point of damage given to non-player enemies. (Default 0.01) ba_enemy_take_damage_score: Score lost per point of damage taken from non-player enemies. (Default 0) ba_player_kill_score: Score earned by killing another player. (Default 10) ba_player_death_score: Score lost by dying to another player. (Default 7) ba_enemy_kill_score: Score earned by killing a non-player enemy. (Default 3) ba_enemy_death_score: Score lost by dying to a non-player enemy. (Default 7) ba_meseta_score: Score earned per Meseta in the player's inventory. (Default 0)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_SET_TRAP = Opcode(
    code = 0xF851,
    mnemonic = "ba_set_trap",
    doc = "Sets the number of traps players can use in battle mode. regsA[1] is the amount; regsA[0] is the trap type: 0 = Damage trap (internal type 0) 1 = Slow trap (internal type 2) 2 = Confuse trap (internal type 3) 3 = Freeze trap (internal type 1)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_HIDE_TARGET_RETICLE = Opcode(
    code = 0xF852,
    mnemonic = "ba_hide_target_reticle",
    doc = "Enables (0) or disables (1) the targeting reticle in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_REVERSE_WARPS = Opcode(
    code = 0xF853,
    mnemonic = "reverse_warps",
    doc = "Enables or disables overrides of warp destination floors. When enabled, area warps will always go to the next floor (current floor + 1); when disabled, they will go to the floor specified in their constructor args.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_UNREVERSE_WARPS = Opcode(
    code = 0xF854,
    mnemonic = "unreverse_warps",
    doc = "Enables or disables overrides of warp destination floors. When enabled, area warps will always go to the next floor (current floor + 1); when disabled, they will go to the floor specified in their constructor args.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_ULT_MAP = Opcode(
    code = 0xF855,
    mnemonic = "set_ult_map",
    doc = "Enables or disables overriding graphical features with those used in Ultimate",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_UNSET_ULT_MAP = Opcode(
    code = 0xF856,
    mnemonic = "unset_ult_map",
    doc = "Enables or disables overriding graphical features with those used in Ultimate",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_AREA_TITLE = Opcode(
    code = 0xF857,
    mnemonic = "set_area_title",
    doc = "Sets the area title in challenge mode",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SHOW_SELF_TRAPS = Opcode(
    code = 0xF858,
    mnemonic = "ba_show_self_traps",
    doc = "Enables or disables the ability to see your own traps.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BA_HIDE_SELF_TRAPS = Opcode(
    code = 0xF859,
    mnemonic = "ba_hide_self_traps",
    doc = "Enables or disables the ability to see your own traps.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_EQUIP_ITEM_V2 = Opcode(
    code = 0xF85A,
    mnemonic = "equip_item",
    doc = "Creates an item in a player's inventory and equips it. V2 inline form: 4 registers (4-byte encoding). (newserv QuestScript.cc:1474)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "item.data1[0-2] [0]", null, true, false),
            Param(IntType, "item.data1[0-2] [1]", null, true, false),
            Param(IntType, "item.data1[0-2] [2]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_EQUIP_ITEM_V3_V4 = Opcode(
    code = 0xF85A,
    mnemonic = "equip_item",
    doc = "Creates an item in a player's inventory and equips it. (newserv QuestScript.cc:1475)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "item.data1[0-2] [0]", null, true, false),
            Param(IntType, "item.data1[0-2] [1]", null, true, false),
            Param(IntType, "item.data1[0-2] [2]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_UNEQUIP_ITEM = Opcode(
    code = 0xF85B,
    mnemonic = "unequip_item",
    doc = "Unequips an item from a client. Sends 6x26 if an item is unequipped. 0 = weapon 1 = armor 2 = shield 3 = mag 4-7 = units 1-4",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(IntType, "equip slot, but not the same as the EquipSlot enum", "equip slot, but not the same as the EquipSlot enum:", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_P_TALK_GUILD_LOCAL = Opcode(
    code = 0xF85C,
    mnemonic = "p_talk_guild_local",
    doc = "Same as p_talk_guild except it always refers to the local player. valueA is ignored.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_ALLOW_ITEM_FLAGS = Opcode(
    code = 0xF85D,
    mnemonic = "set_allow_item_flags",
    doc = "Sets flags that forbid types of items from being used. To forbid multiple types of items, use this opcode multiple times. valueA: 0 = allow normal item usage (undoes all of the following) 1 = disallow weapons 2 = disallow armors 3 = disallow shields 4 = disallow units 5 = disallow mags 6 = disallow tools",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_ENABLE_SONAR = Opcode(
    code = 0xF85E,
    mnemonic = "ba_enable_sonar",
    doc = "Enables (1) or disables (0) sonar in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SONAR_COUNT = Opcode(
    code = 0xF85F,
    mnemonic = "ba_sonar_count",
    doc = "Sets the number of sonar uses per character in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CLEAR_SCORE_ANNOUNCE = Opcode(
    code = 0xF860,
    mnemonic = "clear_score_announce",
    doc = "Specifies when score announcements should occur during battle. The values are measured in minutes remaining. There can be up to 8 score announcements; any further set_score_announce calls are ignored. clear_score_announce deletes all announcement times.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_SCORE_ANNOUNCE = Opcode(
    code = 0xF861,
    mnemonic = "set_score_announce",
    doc = "Specifies when score announcements should occur during battle. The values are measured in minutes remaining. There can be up to 8 score announcements; any further set_score_announce calls are ignored. clear_score_announce deletes all announcement times.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GIVE_S_RANK_WEAPON_V2 = Opcode(
    code = 0xF862,
    mnemonic = "give_s_rank_weapon",
    doc = "Creates an S-rank weapon in the player's inventory. V2 inline form: two register refs (4-byte encoding each) + C-string. (newserv QuestScript.cc:1520)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "item.data1[1]", null, true, false),
        ), 1), null, null, false, false),
        Param(StringType, "custom name", null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_GIVE_S_RANK_WEAPON_V3_V4 = Opcode(
    code = 0xF862,
    mnemonic = "give_s_rank_weapon",
    doc = "Creates an S-rank weapon in the player's inventory. This opcode is not used in challenge mode, presumably since it doesn't offer a mechanism for the player to choose their weapon's name. The award_item_give opcode is used instead. regA/valueA = client ID (must match local client ID) regB (must be a register, even on v3/v4) = item.data1[1]. (newserv QuestScript.cc:1521)",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(StringType, "custom name", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_GET_MAG_LEVELS_V2 = Opcode(
    code = 0xF863,
    mnemonic = "get_mag_levels",
    doc = "Returns the currently-equipped mag's levels. V2 inline form: 4 registers (4-byte encoding). (newserv QuestScript.cc:1529)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "returned DEF level", null, false, true),
            Param(IntType, "returned POW level", null, false, true),
            Param(IntType, "returned DEX level", null, false, true),
            Param(IntType, "returned MIND level", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_GET_MAG_LEVELS_V3_V4 = Opcode(
    code = 0xF863,
    mnemonic = "get_mag_levels",
    doc = "Returns the currently-equipped mag's levels. If no mag is equipped, regsA are unaffected! Make sure to initialize them before using this. (newserv QuestScript.cc:1530)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "returned DEF level", null, false, true),
            Param(IntType, "returned POW level", null, false, true),
            Param(IntType, "returned DEX level", null, false, true),
            Param(IntType, "returned MIND level", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_CMODE_RANK = Opcode(
    code = 0xF864,
    mnemonic = "cmode_rank",
    doc = "Sets the color and rank text if the player manages to complete the current challenge stage",
    params = listOf(
        Param(IntType, "color as XRGB8888", null, false, false),
        Param(StringType, "rank text", "rank text (up to 11 characters)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_AWARD_ITEM_NAME = Opcode(
    code = 0xF865,
    mnemonic = "award_item_name",
    doc = "Shows the item name entry window and suspends the calling thread",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_AWARD_ITEM_SELECT = Opcode(
    code = 0xF866,
    mnemonic = "award_item_select",
    doc = "Shows the item choice window and suspends the calling thread",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_AWARD_ITEM_GIVE_TO = Opcode(
    code = 0xF867,
    mnemonic = "award_item_give_to",
    doc = "Creates an item in the player's inventory, chosen via the previous award_item_name and award_item_select opcodes, and updates the player's challenge rank text and color according to the rank they achieved. Sends 07DF on BB; on other versions, sends nothing",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "return value", "return value (1 if item successfully created; 0 otherwise)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_CMODE_RANK = Opcode(
    code = 0xF868,
    mnemonic = "set_cmode_rank",
    doc = "Set challenge mode rank",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "rank", "rank (0 = B, 1 = A, 2 = S)", true, false),
            Param(IntType, "time in seconds", "time in seconds (times faster than this are considered to be this rank or better)", true, false),
            Param(IntType, "award flags mask", "award flags mask (generally should be (1 << regsA[0]))", true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CHECK_RANK_TIME = Opcode(
    code = 0xF869,
    mnemonic = "check_rank_time",
    doc = "Check rank time threshold",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_CREATE_CMODE = Opcode(
    code = 0xF86A,
    mnemonic = "item_create_cmode",
    doc = "Creates an item in the local player's bank, and saves the player's challenge rank and title color. Sends 07DF on BB. This is used for the A and B rank prizes",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-5]", "first of 6 consecutive registers holding item.data1[0]..item.data1[5]", false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned success code", "returned success code (1 = success, 0 = failed)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_BOX_DROPS = Opcode(
    code = 0xF86B,
    mnemonic = "ba_box_drops",
    doc = "Sets the effective area for item drops in battle. valueA should be in the range [1, 10].",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AWARD_ITEM_OK = Opcode(
    code = 0xF86C,
    mnemonic = "award_item_ok",
    doc = "Shows a confirmation window asking if the player is satsified with their choice of S rank prize and weapon name",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "result code", "result code (0 = OK, 1 = reconsider)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_SET_TRAPSELF = Opcode(
    code = 0xF86D,
    mnemonic = "ba_set_trapself",
    doc = "Enables or disables traps' ability to hurt the player who set them",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BA_CLEAR_TRAPSELF = Opcode(
    code = 0xF86E,
    mnemonic = "ba_clear_trapself",
    doc = "Enables or disables traps' ability to hurt the player who set them",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BA_SET_LIVES = Opcode(
    code = 0xF86F,
    mnemonic = "ba_set_lives",
    doc = "Sets the number of lives each player gets in battle when the LIMIT_LIVES respawn mode is used.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_TECH_LVL = Opcode(
    code = 0xF870,
    mnemonic = "ba_set_tech_lvl",
    doc = "Sets the maximum level for any technique in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_LVL = Opcode(
    code = 0xF871,
    mnemonic = "ba_set_lvl",
    doc = "Sets the character's overlay level in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BA_SET_TIME_LIMIT = Opcode(
    code = 0xF872,
    mnemonic = "ba_set_time_limit",
    doc = "Sets the battle time limit. valueA is measured in minutes",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BOSS_IS_DEAD = Opcode(
    code = 0xF873,
    mnemonic = "boss_is_dead",
    doc = "Sets regA to 1 if Dark Falz has been defeated, or 0 otherwise.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_CMODE_RANK_OVERRIDE = Opcode(
    code = 0xF874,
    mnemonic = "set_cmode_rank_override",
    doc = "Sets an override for the challenge rank text. At the time the rank is checked via check_rank_time, these overrides are applied in the order they were created. For each one, if the existing rank matches the check string, it is replaced with the override string and the color is replaced with the override color",
    params = listOf(
        Param(IntType, "override color", "override color (XRGB8888)", false, false),
        Param(StringType, "check string and override string", "check string and override string (separated by \\t or \\n)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ENABLE_STEALTH_SUIT_EFFECT = Opcode(
    code = 0xF875,
    mnemonic = "enable_stealth_suit_effect",
    doc = "Enables or disables the transparency effect, similar to the Stealth Suit. regA is the client ID.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISABLE_STEALTH_SUIT_EFFECT = Opcode(
    code = 0xF876,
    mnemonic = "disable_stealth_suit_effect",
    doc = "Enables or disables the transparency effect, similar to the Stealth Suit. regA is the client ID.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ENABLE_TECHS = Opcode(
    code = 0xF877,
    mnemonic = "enable_techs",
    doc = "Enables technique use for the given player.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISABLE_TECHS = Opcode(
    code = 0xF878,
    mnemonic = "disable_techs",
    doc = "Disables technique use for the given player.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_GENDER = Opcode(
    code = 0xF879,
    mnemonic = "get_gender",
    doc = "Retrieves the player's gender. 0 If male, 1 if female.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned gender", "returned gender (0 = male, 1 = female, 2 = no player present or invalid class flags)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_CHARA_CLASS = Opcode(
    code = 0xF87A,
    mnemonic = "get_chara_class",
    doc = "Retrieves the player's race and character class. race will be 0 if human, 1 if newman and 2 if cast. class will be 0 if hunter, 1 if ranger and 2 if force.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned race", "returned race (0 = human, 1 = newman, 2 = android, 3 = no player present or invalid class flags)", false, true),
            Param(IntType, "returned class", "returned class (0 = hunter, 1 = ranger, 2 = force, 3 = no player present or invalid class flags)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_TAKE_SLOT_MESETA = Opcode(
    code = 0xF87B,
    mnemonic = "take_slot_meseta",
    doc = "Takes an amount of meseta from a player's inventory. Returns 1 if the meseta was taken, 0 otherwise.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "amount to subtract", "amount to subtract (positive integer)", true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "result code if player is present", "result code if player is present (1 = taken, 0 = player didn't have enough)", false, true),
            Param(IntType, "result code if player not present", "result code if player not present (set to 0 if there is no player with the specified client ID)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_GUILD_CARD_FILE_CREATION_TIME = Opcode(
    code = 0xF87C,
    mnemonic = "get_guild_card_file_creation_time",
    doc = "Returns the Guild Card file creation time in seconds since 00:00:00 on 1 January 2000.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_KILL_PLAYER = Opcode(
    code = 0xF87D,
    mnemonic = "kill_player",
    doc = "Kills the player whose client ID is regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_SERIAL_NUMBER_V2_V3 = Opcode(
    code = 0xF87E,
    mnemonic = "get_serial_number",
    doc = "Returns (in regA) the player's serial number. (newserv QuestScript.cc:1640)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x7C,
    argsMode = ArgsMode.Inline,
)
val OP_RETURN_0_F87E_V4 = Opcode(
    code = 0xF87E,
    mnemonic = "return_0_f87e",
    doc = "On BB, returns 0 in regA. Functionally equivalent to get_serial_number but always returns 0. (newserv QuestScript.cc:1641)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x80,
    argsMode = ArgsMode.Inline,
)
val OP_READ_GUILDCARD_FLAG = Opcode(
    code = 0xF87F,
    mnemonic = "read_guildcard_flag",
    doc = "Reads an event flag from the system file",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "event flag index", "event flag index (0x00-0xFF)", false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned event flag value", "returned event flag value (1 byte)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_TRAP_DAMAGE = Opcode(
    code = 0xF880,
    mnemonic = "set_trap_damage",
    doc = "Normally, trap damage is computed with the following formula: (700.0 * area_factor[area] * 2.0 * (0.01 * level + 0.1)) This opcode overrides that computation. The value is specified with the integer and fractional parts split up: the actual value used by the game will be regsA[0] + (regsA[1] / regsA[2]).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PL_NAME = Opcode(
    code = 0xF881,
    mnemonic = "get_pl_name",
    doc = "Sets the value of <pl_name> to the given player's name.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PL_JOB = Opcode(
    code = 0xF882,
    mnemonic = "get_pl_job",
    doc = "Sets the value of <pl_job> to the given player's job (Hunter/Ranger/Force).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PLAYER_PROXIMITY = Opcode(
    code = 0xF883,
    mnemonic = "get_player_proximity",
    doc = "Counts the number of players near the specified player",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, false, true),
            Param(IntType, "radius", "radius (as integer)", false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_EVENTFLAG16_V2 = Opcode(
    code = 0xF884,
    mnemonic = "set_eventflag16",
    doc = "Writes 2 bytes to the event flags in the system file. V2 inline form: flag index as int, value from register. (newserv QuestScript.cc:1671)",
    params = listOf(
        Param(IntType, "flag index", "flag index (must be 254 or less)", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_SET_EVENTFLAG16_V3_V4 = Opcode(
    code = 0xF884,
    mnemonic = "set_eventflag16",
    doc = "Writes 2 bytes to the event flags in the system file. regB/valueB = value. (newserv QuestScript.cc:1672)",
    params = listOf(
        Param(IntType, "flag index", "flag index (must be 254 or less)", false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_SET_EVENTFLAG32_V2 = Opcode(
    code = 0xF885,
    mnemonic = "set_eventflag32",
    doc = "Writes 4 bytes to the event flags in the system file. V2 inline form: flag index as int, value from register. (newserv QuestScript.cc:1677)",
    params = listOf(
        Param(IntType, "flag index", "flag index (must be 252 or less)", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_SET_EVENTFLAG32_V3_V4 = Opcode(
    code = 0xF885,
    mnemonic = "set_eventflag32",
    doc = "Writes 4 bytes to the event flags in the system file. regB/valueB = value. (newserv QuestScript.cc:1678)",
    params = listOf(
        Param(IntType, "flag index", "flag index (must be 252 or less)", false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_BA_GET_PLACE = Opcode(
    code = 0xF886,
    mnemonic = "ba_get_place",
    doc = "Returns (in regB) the battle result place (1, 2, 3, or 4) of the player specified by regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_GET_SCORE = Opcode(
    code = 0xF887,
    mnemonic = "ba_get_score",
    doc = "Returns (in regB) the battle score of the player specified by regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BA_CLOSE_MSG = Opcode(
    code = 0xF888,
    mnemonic = "ba_close_msg",
    doc = "TODO: Document these",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISABLE_WIN_PFX = Opcode(
    code = 0xF889,
    mnemonic = "disable_win_pfx",
    doc = null,
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_PLAYER_STATUS = Opcode(
    code = 0xF88A,
    mnemonic = "get_player_status",
    doc = "Returns (in regB) the state of the player specified by regA. State values: 0 = no player present, or warping (non-BB) 1 = standing 2 = walking 3 = running 4 = attacking 5 = casting technique 6 = performing photon blast 7 = defending 8 = taking damage / knocked down 9 = dead 10 = cutscene 11 = reviving 12 = frozen 13 = warping (BB only)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SEND_MAIL = Opcode(
    code = 0xF88B,
    mnemonic = "send_mail",
    doc = "Sends a Simple Mail message to the player. regA (v2) = sender's Guild Card number valueA (v3+) = number of register that holds sender's Guild Card number strB = sender name and message (separated by \\n)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(StringType, "sender name and message", "sender name and message (separated by \\n)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ONLINE_CHECK = Opcode(
    code = 0xF88C,
    mnemonic = "online_check",
    doc = "Returns the game's major version (2 on DCv2/PC/GCNTE, 3 on GC/Ep3, 4 on Xbox and BB)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CHL_SET_TIMERECORD_V2_V3 = Opcode(
    code = 0xF88D,
    mnemonic = "chl_set_timerecord",
    doc = "Sets the challenge mode time record. (V2 + V3 form, 1 register; newserv QuestScript.cc:1719) Inline read on V2_V3 (newserv: no F_ARGS).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x7C,
    argsMode = ArgsMode.Inline,
)
val OP_CHL_SET_TIMERECORD_V4 = Opcode(
    code = 0xF88D,
    mnemonic = "chl_set_timerecord",
    doc = "Sets the challenge mode time record. (V4 form, 2 registers; newserv QuestScript.cc:1720) Inline read on V4 (newserv: no F_ARGS).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x80,
    argsMode = ArgsMode.Inline,
)
val OP_CHL_GET_TIMERECORD = Opcode(
    code = 0xF88E,
    mnemonic = "chl_get_timerecord",
    doc = "Gets the current player's completion time for the current challenge stage in seconds. If the player's time is invalid or faster than the time set by chl_set_min_time_online (or 5 minutes, if offline), returns -2. If used in non-challenge mode, returns -1.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_CMODE_GRAVE_RATES = Opcode(
    code = 0xF88F,
    mnemonic = "set_cmode_grave_rates",
    doc = "Set challenge mode grave/penalty rates",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CLEAR_MAINWARP_ALL = Opcode(
    code = 0xF890,
    mnemonic = "clear_mainwarp_all",
    doc = "Clears all levels from the main warp.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LOAD_ENEMY_DATA = Opcode(
    code = 0xF891,
    mnemonic = "load_enemy_data",
    doc = "Specifies which enemy should be affected by subsequent get_*_data opcodes (the following 4 definitions). valueA is the battle parameter index for the desired enemy.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_PHYSICAL_DATA = Opcode(
    code = 0xF892,
    mnemonic = "get_physical_data",
    doc = "Replaces enemy stats with the given structures (PlayerStats, AttackData, ResistData, or MovementData) for the enemy previously specified with load_enemy_data.",
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_ATTACK_DATA = Opcode(
    code = 0xF893,
    mnemonic = "get_attack_data",
    doc = "Replaces enemy stats with the given structures (PlayerStats, AttackData, ResistData, or MovementData) for the enemy previously specified with load_enemy_data.",
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_RESIST_DATA = Opcode(
    code = 0xF894,
    mnemonic = "get_resist_data",
    doc = "Replaces enemy stats with the given structures (PlayerStats, AttackData, ResistData, or MovementData) for the enemy previously specified with load_enemy_data.",
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_MOVEMENT_DATA = Opcode(
    code = 0xF895,
    mnemonic = "get_movement_data",
    doc = "Replaces enemy stats with the given structures (PlayerStats, AttackData, ResistData, or MovementData) for the enemy previously specified with load_enemy_data.",
    params = listOf(
        Param(DLabelType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_EVENTFLAG16 = Opcode(
    code = 0xF896,
    mnemonic = "get_eventflag16",
    doc = "Reads 2 bytes or 4 bytes from the event flags in the system file",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "event flag index", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned value", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_EVENTFLAG32 = Opcode(
    code = 0xF897,
    mnemonic = "get_eventflag32",
    doc = "Reads 2 bytes or 4 bytes from the event flags in the system file.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SHIFT_LEFT = Opcode(
    code = 0xF898,
    mnemonic = "shift_left",
    doc = "regA <<= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SHIFT_RIGHT = Opcode(
    code = 0xF899,
    mnemonic = "shift_right",
    doc = "regA >>= regB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_RANDOM = Opcode(
    code = 0xF89A,
    mnemonic = "get_random",
    doc = "Generates a random number by calling rand(). Note that the returned value is not uniform! The algorithm generates a uniform random number, scales it to the range 0 through (max-1) inclusive, then clamps it to the range min through (max-1). So, if the minimum value is not 0, the minimum value is more likely than all other possible results. To get an unbiased result with a minimum not equal to zero, use this with a minimum of zero, then use add or addi. Note also that this is implemented by calling rand(), which returns a 15-bit random integer. If your range of values is larger than 15 bits, call this multiple times and combine the results appropriately",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "minimum value", null, true, false),
            Param(IntType, "maximum value", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_RESET_MAP = Opcode(
    code = 0xF89B,
    mnemonic = "reset_map",
    doc = "Sets all registers to 0 and resets the quest.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_DISP_CHL_RETRY_MENU = Opcode(
    code = 0xF89C,
    mnemonic = "disp_chl_retry_menu",
    doc = "Returns the leader's choice when a challenge is failed in regA. Values: 0 = not chosen yet 1 = no (releases players to interact on Pioneer 2) 2 = yes (restarts the stage)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CHL_REVERSER = Opcode(
    code = 0xF89D,
    mnemonic = "chl_reverser",
    doc = "Creates the retry menu when a challenge stage is failed",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BA_FORBID_SCAPE_DOLLS = Opcode(
    code = 0xF89E,
    mnemonic = "ba_forbid_scape_dolls",
    doc = "Enables (0) or disables (1) the use of scape dolls in battle",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PLAYER_RECOVERY = Opcode(
    code = 0xF89F,
    mnemonic = "player_recovery",
    doc = "Restores a player's HP and TP, clears status effects, and revives the player if dead",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISABLE_BOSSWARP_OPTION = Opcode(
    code = 0xF8A0,
    mnemonic = "disable_bosswarp_option",
    doc = "These opcodes set, clear, and check (respectively) a flag that appears to do nothing at all.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_ENABLE_BOSSWARP_OPTION = Opcode(
    code = 0xF8A1,
    mnemonic = "enable_bosswarp_option",
    doc = "These opcodes set, clear, and check (respectively) a flag that appears to do nothing at all.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_IS_BOSSWARP_OPT_DISABLED = Opcode(
    code = 0xF8A2,
    mnemonic = "is_bosswarp_opt_disabled",
    doc = "These opcodes set, clear, and check (respectively) a flag that appears to do nothing at all.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_LOAD_SERIAL_NUMBER_TO_FLAG_BUF = Opcode(
    code = 0xF8A3,
    mnemonic = "load_serial_number_to_flag_buf",
    doc = "Loads the player's serial number into the \"flag buffer\", which is a 4-byte buffer that can be written to event flags. It's not obvious why this can't just be done with get_serial_number and set_eventflag32. This opcode loads 0 to the flag buffer on BB.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WRITE_FLAG_BUF_TO_EVENT_FLAGS = Opcode(
    code = 0xF8A4,
    mnemonic = "write_flag_buf_to_event_flags",
    doc = "Writes the flag buffer to event flags. regA specifies which event flag (the first of 4 consecutive flags).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_CHAT_CALLBACK_NO_FILTER = Opcode(
    code = 0xF8A5,
    mnemonic = "set_chat_callback_no_filter",
    doc = "Like set_chat_callback, but without a filter string. The meaning of regsA is the same as for set_chat_callback.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SYMBOL_CHAT_COLLISION = Opcode(
    code = 0xF8A6,
    mnemonic = "set_symbol_chat_collision",
    doc = "Creates a symbol chat collision object. See the description of TOSymbolchatColli in Map.cc for details on how this object behaves and what these arguments mean. PlayerSubordinates.hh). Note that this structure is not byteswapped properly, so GameCube quests that use this opcode should use the big-endian version of the struct. (Practically, this means the first 32-bit field and the following 4 16-bit fields must be byteswapped.)",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, "location X", null, true, false),
            Param(FloatType, "location Y", null, true, false),
            Param(FloatType, "location Z", null, true, false),
            Param(FloatType, "radius", null, true, false),
            Param(IntType, "specs 0-2 [0]", "specs 0-2 (see TOSymbolchatColli description)", true, false),
            Param(IntType, "specs 0-2 [1]", "specs 0-2 (see TOSymbolchatColli description)", true, false),
            Param(IntType, "specs 0-2 [2]", "specs 0-2 (see TOSymbolchatColli description)", true, false),
            Param(IntType, "data labels for symbol chats 0-2; each points to a SymbolChat structure [0]", "data labels for symbol chats 0-2; each points to a SymbolChat structure (see SymbolChatT in", true, false),
            Param(IntType, "data labels for symbol chats 0-2; each points to a SymbolChat structure [1]", "data labels for symbol chats 0-2; each points to a SymbolChat structure (see SymbolChatT in", true, false),
            Param(IntType, "data labels for symbol chats 0-2; each points to a SymbolChat structure [2]", "data labels for symbol chats 0-2; each points to a SymbolChat structure (see SymbolChatT in", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SHRINK_SIZE = Opcode(
    code = 0xF8A7,
    mnemonic = "set_shrink_size",
    doc = "Sets the size that a player shrinks to when using the shrink opcode. regA specified the client ID. The actual shrink size used is regsB[0] + (regsB[1] / regsB[2]). If regsB[2] is 0, the fractional part is considered to be zero and not used.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DEATH_TECH_LVL_UP2 = Opcode(
    code = 0xF8A8,
    mnemonic = "death_tech_lvl_up2",
    doc = "Sets the amount by which techniques level up upon respawn in battle.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_VOL_OPT_IS_DEAD = Opcode(
    code = 0xF8A9,
    mnemonic = "vol_opt_is_dead",
    doc = "Returns 1 if Vol Opt has been defeated in the current game/quest.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_IS_THERE_GRAVE_MESSAGE = Opcode(
    code = 0xF8AA,
    mnemonic = "is_there_grave_message",
    doc = "Returns 1 if the local player has a challenge mode grave message.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_BA_RECORD = Opcode(
    code = 0xF8AB,
    mnemonic = "get_ba_record",
    doc = "Returns the local player's battle mode records. The values returned are the first 7 fields of the PlayerRecordsBattle structure (see PlayerSubordinates.hh). These are",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "number of times placed 1st, 2nd, 3rd, and 4th, respectively [0]", null, true, false),
            Param(IntType, "number of times placed 1st, 2nd, 3rd, and 4th, respectively [1]", null, true, false),
            Param(IntType, "number of times placed 1st, 2nd, 3rd, and 4th, respectively [2]", null, true, false),
            Param(IntType, "number of times placed 1st, 2nd, 3rd, and 4th, respectively [3]", null, true, false),
            Param(IntType, "number of disconnects", null, true, false),
            Param(IntType, "unknown [0]", "unknown (TODO)", true, false),
            Param(IntType, "unknown [1]", "unknown (TODO)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_CMODE_PRIZE_RANK = Opcode(
    code = 0xF8AC,
    mnemonic = "get_cmode_prize_rank",
    doc = "Returns the current player's challenge mode rank. Reads from the state corresponding to the current game mode (that is, reads from online Ep1 records in online Ep1 challenge mode, reads from offline Ep1 records in offline challenge mode, reads from Ep2 records in Ep2). Return values: 0 = challenge mode not completed 1 = B rank 2 = A rank 3 = S rank",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_NUMBER_OF_PLAYER2 = Opcode(
    code = 0xF8AD,
    mnemonic = "get_number_of_player2",
    doc = "Returns the number of players (in regA). Unlike get_number_of_players, this counts the number of objects that have entity IDs assigned in the players' ID space, whereas get_number_of_players counds the number of TObjPlayer objects. For all practical purposes, these should result in the same number.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_PARTY_HAS_NAME = Opcode(
    code = 0xF8AE,
    mnemonic = "party_has_name",
    doc = "Returns 1 (in regA) if the current game has a nonempty name. The game name is set by command 8A from the server.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SOMEONE_HAS_SPOKEN = Opcode(
    code = 0xF8AF,
    mnemonic = "someone_has_spoken",
    doc = "Returns 1 (in regA) if there is a chat message available (that is, if anyone has sent a chat message in the current game).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_READ1_V2 = Opcode(
    code = 0xF8B0,
    mnemonic = "read1",
    doc = "Reads a 1-byte value from an arbitrary location. V2 inline form: result register + address in a second register. (newserv QuestScript.cc:1873)",
    params = listOf(
        Param(RegType(listOf(
            Param(ByteType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to read from.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_READ1_V3_V4 = Opcode(
    code = 0xF8B0,
    mnemonic = "read1",
    doc = "Reads a 1-byte value from an arbitrary location. (newserv QuestScript.cc:1874)",
    params = listOf(
        Param(RegType(listOf(
            Param(ByteType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(IntType, null, "Address to read from.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_READ2_V2 = Opcode(
    code = 0xF8B1,
    mnemonic = "read2",
    doc = "Reads a 2-byte value from an arbitrary location. V2 inline form: result register + address in a second register. (newserv QuestScript.cc:1875)",
    params = listOf(
        Param(RegType(listOf(
            Param(ShortType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to read from.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_READ2_V3_V4 = Opcode(
    code = 0xF8B1,
    mnemonic = "read2",
    doc = "Reads a 2-byte value from an arbitrary location. (newserv QuestScript.cc:1876)",
    params = listOf(
        Param(RegType(listOf(
            Param(ShortType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(IntType, null, "Address to read from.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_READ4_V2 = Opcode(
    code = 0xF8B2,
    mnemonic = "read4",
    doc = "Reads a 4-byte value from an arbitrary location. V2 inline form: result register + address in a second register. (newserv QuestScript.cc:1877)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to read from.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_READ4_V3_V4 = Opcode(
    code = 0xF8B2,
    mnemonic = "read4",
    doc = "Reads a 4-byte value from an arbitrary location. (newserv QuestScript.cc:1878)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, "Register to store the result to.", false, false),
        Param(IntType, null, "Address to read from.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_WRITE1_V2 = Opcode(
    code = 0xF8B3,
    mnemonic = "write1",
    doc = "Writes a 1-byte value to an arbitrary location. V2 inline form: address in register + value in register. (newserv QuestScript.cc:1883)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to write to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding value to write (low byte used).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_WRITE1_V3_V4 = Opcode(
    code = 0xF8B3,
    mnemonic = "write1",
    doc = "Writes a 1-byte value to an arbitrary location. (newserv QuestScript.cc:1884)",
    params = listOf(
        Param(IntType, null, "Address to write to.", false, false),
        Param(IntType, null, "Value to be written (low byte used).", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_WRITE2_V2 = Opcode(
    code = 0xF8B4,
    mnemonic = "write2",
    doc = "Writes a 2-byte value to an arbitrary location. V2 inline form: address in register + value in register. (newserv QuestScript.cc:1885)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to write to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding value to write (low 2 bytes used).", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_WRITE2_V3_V4 = Opcode(
    code = 0xF8B4,
    mnemonic = "write2",
    doc = "Writes a 2-byte value to an arbitrary location. (newserv QuestScript.cc:1886)",
    params = listOf(
        Param(IntType, null, "Address to write to.", false, false),
        Param(IntType, null, "Value to be written (low 2 bytes used).", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_WRITE4_V2 = Opcode(
    code = 0xF8B5,
    mnemonic = "write4",
    doc = "Writes a 4-byte value to an arbitrary location. V2 inline form: address in register + value in register. (newserv QuestScript.cc:1887)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding address to write to.", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, "Register holding value to write.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_WRITE4_V3_V4 = Opcode(
    code = 0xF8B5,
    mnemonic = "write4",
    doc = "Writes a 4-byte value to an arbitrary location. (newserv QuestScript.cc:1888)",
    params = listOf(
        Param(IntType, null, "Address to write to.", false, false),
        Param(IntType, null, "Value to be written.", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Stack,
)
val OP_CHECK_FOR_HACKING = Opcode(
    code = 0xF8B6,
    mnemonic = "check_for_hacking",
    doc = "Returns a bitmask of 5 different types of detectable hacking. This opcode only works on DCv2 - it crashes on all other versions, since it tries to access memory at 8C007220, which is only a valid address on DC. The bits are: 0x01 = any byte in [8C007220, 8C0072E0) is nonzero (crashes on non-DC) 0x02 = has cheat device save files on VMU (always zero on non-DC); looks for names: CDX_CODES_00, CDX_SETTINGS, XT-CHEATS, FCDCHEATS 0x04 = any of the first 3 VMU-like devices (with function_code & 0x0E not equal to zero) on any of the 4 Maple buses reports power usage (standby or max) above 4 amps; always zero on non-DC systems 0x08 = hacked item flag has been set (see implementation of are_rare_drops_allowed in ItemCreator.cc) 0x10 = any bits in validation_flags in the character file are set (see PSOGCCharacterFile::Character)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CHL_SET_MIN_TIME_ONLINE = Opcode(
    code = 0xF8B7,
    mnemonic = "chl_set_min_time_online",
    doc = "Challenge mode cannot be completed unless this many seconds have passed since the stage began. If not set or if offline, 5 minutes is used as the threshold instead.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_DISABLE_RETRY_MENU = Opcode(
    code = 0xF8B8,
    mnemonic = "disable_retry_menu",
    doc = "Disables the challenge mode retry menu",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_CHL_RECOVERY = Opcode(
    code = 0xF8B9,
    mnemonic = "chl_recovery",
    doc = "Shows the list of dead players in challenge mode",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LOAD_GUILD_CARD_FILE_CREATION_TIME_TO_FLAG_BUF = Opcode(
    code = 0xF8BA,
    mnemonic = "load_guild_card_file_creation_time_to_flag_buf",
    doc = "Loads the Guild Card file creation time to the flag buffer. (See F8A3 and F8A4 for more details.)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_WRITE_FLAG_BUF_TO_EVENT_FLAGS2 = Opcode(
    code = 0xF8BB,
    mnemonic = "write_flag_buf_to_event_flags2",
    doc = "Behaves exactly the same as write_flag_buf_to_event_flags (F8A4).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PLAYER_GUILD_CARD_NUMBER_V2 = Opcode(
    code = 0xF8BC,
    mnemonic = "get_player_guild_card_number",
    doc = "Returns (in regB) the Guild Card number of the player in the slot specified by regA. If there is no player in that slot, returns FFFFFFFF. This opcode is only implemented on PC v2 (not on DC v2 or other V2 versions). Modeled as V2 in psolib for lack of a PC_V2-only shortcut. (newserv QuestScript.cc:1920)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID slot", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned guild card number", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x3C,
    argsMode = ArgsMode.Inline,
)
val OP_SET_EPISODE_V3_V4 = Opcode(
    code = 0xF8BC,
    mnemonic = "set_episode",
    doc = "Sets the episode number for this quest. (newserv QuestScript.cc:1925)",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xC0,
    argsMode = ArgsMode.Inline,
)
val OP_GET_CURRENT_SYMBOL_CHAT = Opcode(
    code = 0xF8BD,
    mnemonic = "get_current_symbol_chat",
    doc = "Returns (in regsB) the full symbol chat data for the symbol chat currently being said by the player specified in regA. The symbol chat data is only returned for 120 frames (4 seconds) after the corresponding 6x07 command is received; after that, this opcode will return a blank symbol chat instead. This opcode only works if create_symbol_chat_monitor is run first. This opcode is only implemented on certain later versions of PC v2, and not on any v3 or later versions.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "symbol chat data [0]", null, false, true),
            Param(IntType, "symbol chat data [1]", null, false, true),
            Param(IntType, "symbol chat data [2]", null, false, true),
            Param(IntType, "symbol chat data [3]", null, false, true),
            Param(IntType, "symbol chat data [4]", null, false, true),
            Param(IntType, "symbol chat data [5]", null, false, true),
            Param(IntType, "symbol chat data [6]", null, false, true),
            Param(IntType, "symbol chat data [7]", null, false, true),
            Param(IntType, "symbol chat data [8]", null, false, true),
            Param(IntType, "symbol chat data [9]", null, false, true),
            Param(IntType, "symbol chat data [10]", null, false, true),
            Param(IntType, "symbol chat data [11]", null, false, true),
            Param(IntType, "symbol chat data [12]", null, false, true),
            Param(IntType, "symbol chat data [13]", null, false, true),
            Param(IntType, "symbol chat data [14]", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CREATE_SYMBOL_CHAT_MONITOR = Opcode(
    code = 0xF8BE,
    mnemonic = "create_symbol_chat_monitor",
    doc = "Enables the usage of get_current_symbol_chat. This opcode is only implemented on certain later versions of PC v2, and not on any v3 or later versions.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SAVE_SYSTEM_AND_GC_FILES = Opcode(
    code = 0xF8BF,
    mnemonic = "save_system_and_gc_files",
    doc = "Causes the client to immediately save the PSO______COM (system) and PSO______GUD (Guild Card) files to disk. This opcode is only implemented on certain later versions of PC v2, and not on v3 or later versions.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_FILE_DL_REQ = Opcode(
    code = 0xF8C0,
    mnemonic = "file_dl_req",
    doc = "Requests a file from the server by sending a D7 command. valueA specifies header.flag, strB is the file name (up to 16 characters). This opcode works on Xbox, but the GBA opcodes do not, so it's ultimately not useful there. This opcode does nothing on BB.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_DL_STATUS_V3 = Opcode(
    code = 0xF8C1,
    mnemonic = "get_dl_status",
    doc = "Returns the status of the download requested with file_dl_req. Return values (in regA): 0 = failed (server sent a D7 command) 1 = pending 2 = complete. (newserv QuestScript.cc:1952)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x40,
    argsMode = ArgsMode.Inline,
)
val OP_NOP_F8C1_V4 = Opcode(
    code = 0xF8C1,
    mnemonic = "nop_f8c1",
    doc = "Does nothing on BB. (newserv QuestScript.cc:1953)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x80,
    argsMode = ArgsMode.Inline,
)
val OP_GBA_UNKNOWN4 = Opcode(
    code = 0xF8C2,
    mnemonic = "gba_unknown4",
    doc = "Prepares to load a GBA ROM from a previous file_dl_req opcode. Does nothing on Xbox and BB.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_GBA_STATE = Opcode(
    code = 0xF8C3,
    mnemonic = "get_gba_state",
    doc = "Starts loading a GBA ROM to a connected GBA, or checks the status of a previous load request. One of prepare_gba_rom_from_download or prepare_gba_rom_from_disk must be called before calling this, then this should be called repeatedly until it succeeds or fails. Return values: 0 = not started 1 = failed 2 = timed out 3 = in progress 4 = complete This opcode always returns 0 on Xbox, and does nothing (doesn't even affect regA) on BB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CONGRATS_MSG_MULTI_CM = Opcode(
    code = 0xF8C4,
    mnemonic = "congrats_msg_multi_cm",
    doc = "Shows the challenge mode result window in split-screen mode. Does nothing on BB",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "completion time in seconds", "completion time in seconds, as returned by chl_get_timerecord", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_STAGE_IN_PROGRESS_MULTI_CM_V3 = Opcode(
    code = 0xF8C5,
    mnemonic = "stage_in_progress_multi_cm",
    doc = "Checks if the stage is done in offline challenge mode. Returns 1 if the stage is still in progress, or 0 if it's completed or failed. (newserv QuestScript.cc:1979)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x40,
    argsMode = ArgsMode.Inline,
)
val OP_NOP_F8C5_V4 = Opcode(
    code = 0xF8C5,
    mnemonic = "nop_f8c5",
    doc = "Does nothing on BB. (newserv QuestScript.cc:1980)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0x80,
    argsMode = ArgsMode.Inline,
)
val OP_QEXIT = Opcode(
    code = 0xF8C6,
    mnemonic = "qexit",
    doc = "Causes a fade to black, then exits the game. This is the same result as receiving a 6x73 command.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_USE_ANIMATION = Opcode(
    code = 0xF8C7,
    mnemonic = "use_animation",
    doc = "Causes a player to perform an animation.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "animation ID", null, true, false),
            Param(IntType, "duration in frames", null, true, false),
        ), 1), null, "Animation ID and duration in number of frames.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_STOP_ANIMATION = Opcode(
    code = 0xF8C8,
    mnemonic = "stop_animation",
    doc = "Stops an animation started with use_animation",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_RUN_TO_COORD = Opcode(
    code = 0xF8C9,
    mnemonic = "run_to_coord",
    doc = "Causes a player to run to a location, as 6x42 does. Sends 6x42",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_INVINCIBLE = Opcode(
    code = 0xF8CA,
    mnemonic = "set_slot_invincible",
    doc = "Makes a player invincible, or removes their invincibility",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "enable invincibility", "enable invincibility (1 = enable, 0 = disable)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_TARGETABLE = Opcode(
    code = 0xF8CB,
    mnemonic = "set_slot_targetable",
    doc = "Removes a player's invicibility. clear_slot_invincible rXX is equivalent to set_slot_invincible rXX, 0",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_POISON = Opcode(
    code = 0xF8CC,
    mnemonic = "set_slot_poison",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_PARALYZE = Opcode(
    code = 0xF8CD,
    mnemonic = "set_slot_paralyze",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_SHOCK = Opcode(
    code = 0xF8CE,
    mnemonic = "set_slot_shock",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_FREEZE = Opcode(
    code = 0xF8CF,
    mnemonic = "set_slot_freeze",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_SLOW = Opcode(
    code = 0xF8D0,
    mnemonic = "set_slot_slow",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_CONFUSE = Opcode(
    code = 0xF8D1,
    mnemonic = "set_slot_confuse",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_SHIFTA = Opcode(
    code = 0xF8D2,
    mnemonic = "set_slot_shifta",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_DEBAND = Opcode(
    code = 0xF8D3,
    mnemonic = "set_slot_deband",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_JELLEN = Opcode(
    code = 0xF8D4,
    mnemonic = "set_slot_jellen",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_SLOT_ZALURE = Opcode(
    code = 0xF8D5,
    mnemonic = "set_slot_zalure",
    doc = "These opcodes inflict various status conditions on a player. In the case of Shifta/Deband/Jellen/Zalure, the effective technique level is 21.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FLETI_FIXED_CAMERA = Opcode(
    code = 0xF8D6,
    mnemonic = "fleti_fixed_camera",
    doc = "Same as leti_fixed_camera, but takes floating-point arguments",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "camera location X", null, true, false),
            Param(IntType, "camera location Y", null, true, false),
            Param(IntType, "camera location Z", null, true, false),
            Param(IntType, "camera focus location X", null, true, false),
            Param(IntType, "camera focus location Y", null, true, false),
            Param(IntType, "camera focus location Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_FLETI_LOCKED_CAMERA = Opcode(
    code = 0xF8D7,
    mnemonic = "fleti_locked_camera",
    doc = "Sets the camera to follow the player at a fixed angle",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "camera angle X", null, true, false),
            Param(IntType, "camera angle Y", null, true, false),
            Param(IntType, "camera angle Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DEFAULT_CAMERA_POS2 = Opcode(
    code = 0xF8D8,
    mnemonic = "default_camera_pos2",
    doc = "This opcode appears to be exactly the same as default_camera_pos. TODO: Is there any difference?",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_MOTION_BLUR = Opcode(
    code = 0xF8D9,
    mnemonic = "set_motion_blur",
    doc = "Enables a motion blur visual effect.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_SCREEN_BW = Opcode(
    code = 0xF8DA,
    mnemonic = "set_screen_bw",
    doc = "Enables a monochrome visual effect.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_VECTOR_FROM_PATH = Opcode(
    code = 0xF8DB,
    mnemonic = "get_vector_from_path",
    doc = "Computes a point along a path composed of multiple control points",
    params = listOf(
        Param(IntType, "number of control points", null, false, false),
        Param(IntType, "speed along path", null, false, false),
        Param(IntType, "current position along path", null, false, false),
        Param(IntType, "loop flag", "loop flag (0 = no, 1 = yes)", false, false),
        Param(RegType(listOf(
            Param(IntType, "result point X", null, true, false),
            Param(IntType, "result point Y", null, true, false),
            Param(IntType, "result point Z", null, true, false),
            Param(IntType, "result code", "result code (0 = failed, 1 = success)", true, false),
        ), 1), null, null, false, false),
        Param(DLabelType, "control point entries", "control point entries (array of valueA VectorXYZTF structures)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NPC_ACTION_STRING = Opcode(
    code = 0xF8DC,
    mnemonic = "npc_action_string",
    doc = "Same as npc_text, but only applies to a specific player slot",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(SLabelType, "string for NPC to say", "string for NPC to say (up to 52 characters)", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PAD_COND = Opcode(
    code = 0xF8DD,
    mnemonic = "get_pad_cond",
    doc = "Returns a bitmask of the buttons which are currently pressed or held on this frame",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "controller port number", null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned button flags", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_BUTTON_COND = Opcode(
    code = 0xF8DE,
    mnemonic = "get_button_cond",
    doc = "Returns a bitmask of the buttons which were newly pressed on this frame. Buttons which were pressed on previous frames and still held down are not returned. Same arguments as get_held_buttons.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FREEZE_ENEMIES = Opcode(
    code = 0xF8DF,
    mnemonic = "freeze_enemies",
    doc = "Freezes enemies and makes them untargetable, or unfreezes them and makes them targetable again. Internally, this toggles a flag that disables updates for every child object of TL_04.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_UNFREEZE_ENEMIES = Opcode(
    code = 0xF8E0,
    mnemonic = "unfreeze_enemies",
    doc = "Unfreezes enemies and makes them targetable again.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_FREEZE_EVERYTHING = Opcode(
    code = 0xF8E1,
    mnemonic = "freeze_everything",
    doc = "Freezes (almost) everything, or unfreezes (almost) everything. Internally, this toggles the disable-updates flag for every child of all root objects except TL_SU, TL_00, TL_CAMERA, and TL_01.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_UNFREEZE_EVERYTHING = Opcode(
    code = 0xF8E2,
    mnemonic = "unfreeze_everything",
    doc = "Unfreezes (almost) everything, if toggle_freeze_everything has been executed before.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_RESTORE_HP = Opcode(
    code = 0xF8E3,
    mnemonic = "restore_hp",
    doc = "Sets a player's HP or TP to their maximum HP or TP",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_RESTORE_TP = Opcode(
    code = 0xF8E4,
    mnemonic = "restore_tp",
    doc = "Sets a player's HP or TP to their maximum HP or TP.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CLOSE_CHAT_BUBBLE = Opcode(
    code = 0xF8E5,
    mnemonic = "close_chat_bubble",
    doc = "Closes a chat bubble for a player, if one is open",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_MOVE_COORDS_OBJECT = Opcode(
    code = 0xF8E6,
    mnemonic = "move_coords_object",
    doc = "Moves a dynamic collision object",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AT_COORDS_CALL_EX = Opcode(
    code = 0xF8E7,
    mnemonic = "at_coords_call_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_AT_COORDS_TALK_EX = Opcode(
    code = 0xF8E8,
    mnemonic = "at_coords_talk_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_COORDS_CALL_EX = Opcode(
    code = 0xF8E9,
    mnemonic = "npc_coords_call_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_PARTY_COORDS_CALL_EX = Opcode(
    code = 0xF8EA,
    mnemonic = "party_coords_call_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SET_OBJ_PARAM_EX = Opcode(
    code = 0xF8EB,
    mnemonic = "set_obj_param_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NPC_CHECK_STRAGGLE_EX = Opcode(
    code = 0xF8EC,
    mnemonic = "npc_check_straggle_ex",
    doc = "These are the same as their counterparts without _ex, but these also return the object ID (which is distinct from the object token) in regB. There is a bug in set_obj_param_ex - the object token is not returned anywhere, and only the object ID is returned in regB.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ANIMATION_CHECK = Opcode(
    code = 0xF8ED,
    mnemonic = "animation_check",
    doc = "Returns 1 if the player is doing certain animations. (TODO: Which ones?)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned value", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_CALL_IMAGE_DATA = Opcode(
    code = 0xF8EE,
    mnemonic = "call_image_data",
    doc = "Specifies which image to use for the image board in Pioneer 2 (if placed). Only one image may be loaded at a time. Images must be square and dimensions must be a power of two, similar to the B9 command on Ep3. On DC or PCv2, the image data is expected to be a PVR file; on GC, it should be a GVR file; on Xbox and BB it should be an XVR file",
    params = listOf(
        Param(IntType, "decompressed size", null, false, false),
        Param(DLabelType, "label containing PRS-compressed image file data", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_NOP_F8EF = Opcode(
    code = 0xF8EF,
    mnemonic = "nop_f8ef",
    doc = "This opcode does nothing on all versions where it's implemented.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_TURN_OFF_BGM_P2 = Opcode(
    code = 0xF8F0,
    mnemonic = "turn_off_bgm_p2",
    doc = "Disables or enables the background music on Pioneer 2. If the BGM has not been overridden with create_bgmctrl and set_bgm, the music will resume at the points where it would normally change (e.g. entering or leaving the shop area). turn_off_bgm_p2 may be repeated every frame to avoid this consequence.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_TURN_ON_BGM_P2 = Opcode(
    code = 0xF8F1,
    mnemonic = "turn_on_bgm_p2",
    doc = "Disables or enables the background music on Pioneer 2. If the BGM has not been overridden with create_bgmctrl and set_bgm, the music will resume at the points where it would normally change (e.g. entering or leaving the shop area). turn_off_bgm_p2 may be repeated every frame to avoid this consequence.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_LOAD_UNK_DATA = Opcode(
    code = 0xF8F2,
    mnemonic = "load_unk_data",
    doc = "Computes a point along a Bezier curve defined by a sequence of vectors. This is similar to get_vector_from_path, but results in a smoother curve. Arguments",
    params = listOf(
        Param(IntType, "number of control points", null, false, false),
        Param(IntType, "speed along path", null, false, false),
        Param(IntType, "current position along path", null, false, false),
        Param(IntType, "loop flag", "loop flag (0 = no, 1 = yes)", false, false),
        Param(RegType(listOf(
            Param(IntType, "result point X", null, true, false),
            Param(IntType, "result point Y", null, true, false),
            Param(IntType, "result point Z", null, true, false),
            Param(IntType, "result code", "result code (0 = failed, 1 = success)", true, false),
        ), 1), null, null, false, false),
        Param(DLabelType, "control point entries", "control point entries (array of valueA VectorXYZTF structures)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PARTICLE2 = Opcode(
    code = 0xF8F3,
    mnemonic = "particle2",
    doc = "Creates a timed particle emitter. Coordinates come from integer registers and are numerically converted to float; the immediate float duration is truncated toward zero",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
        ), 1), null, null, false, false),
        Param(IntType, "particle effect ID", null, false, false),
        Param(FloatType, "duration as float", "duration as float (in frames; 30 frames/sec)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DEC2FLOAT = Opcode(
    code = 0xF901,
    mnemonic = "dec2float",
    doc = "Converts the integer in regB into a float in regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FLOAT2DEC = Opcode(
    code = 0xF902,
    mnemonic = "float2dec",
    doc = "Converts the float in regB into an integer in regA.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FLET = Opcode(
    code = 0xF903,
    mnemonic = "flet",
    doc = "These are the same as let and leti. Nominally regB/valueB should be a float, but the implementation treats it as an int (which is still correct, since the float is already encoded before the handler function is called).",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FLETI = Opcode(
    code = 0xF904,
    mnemonic = "fleti",
    doc = "These are the same as let and leti. Nominally regB/valueB should be a float, but the implementation treats it as an int (which is still correct, since the float is already encoded before the handler function is called).",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FADD = Opcode(
    code = 0xF908,
    mnemonic = "fadd",
    doc = "regA += regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FADDI = Opcode(
    code = 0xF909,
    mnemonic = "faddi",
    doc = "regA += regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FSUB = Opcode(
    code = 0xF90A,
    mnemonic = "fsub",
    doc = "regA -= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FSUBI = Opcode(
    code = 0xF90B,
    mnemonic = "fsubi",
    doc = "regA -= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FMUL = Opcode(
    code = 0xF90C,
    mnemonic = "fmul",
    doc = "regA *= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FMULI = Opcode(
    code = 0xF90D,
    mnemonic = "fmuli",
    doc = "regA *= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FDIV = Opcode(
    code = 0xF90E,
    mnemonic = "fdiv",
    doc = "regA /= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(FloatType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FDIVI = Opcode(
    code = 0xF90F,
    mnemonic = "fdivi",
    doc = "regA /= regB (or valueB), as floats",
    params = listOf(
        Param(RegType(listOf(
            Param(FloatType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_UNKNOWN_COUNT = Opcode(
    code = 0xF910,
    mnemonic = "get_unknown_count",
    doc = "Returns the number of times a player has ever died - not just in the current quest/game/session!",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_STACKABLE_ITEM_COUNT = Opcode(
    code = 0xF911,
    mnemonic = "get_stackable_item_count",
    doc = "Returns the stack size of the specified item in the player's inventory",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "client ID", null, true, false),
            Param(IntType, "item.data1[0-2] [0]", null, true, false),
            Param(IntType, "item.data1[0-2] [1]", null, true, false),
            Param(IntType, "item.data1[0-2] [2]", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FREEZE_AND_HIDE_EQUIP = Opcode(
    code = 0xF912,
    mnemonic = "freeze_and_hide_equip",
    doc = "Freezes a character and hides their equips, or does the opposite. Internally, this toggles the disable-update flag on TL_03.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_THAW_AND_SHOW_EQUIP = Opcode(
    code = 0xF913,
    mnemonic = "thaw_and_show_equip",
    doc = "Undoes the effect of toggle_freeze_and_hide_equip, if it has been run an odd number of times. (Otherwise, does nothing.)",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_SET_PALETTEX_CALLBACK = Opcode(
    code = 0xF914,
    mnemonic = "set_palettex_callback",
    doc = "These opcodes are generally used in the following sequence: set_palettex_callback r:client_id, callback_label activate_palettex r:client_id enable_palettex r:client_id ... (X button now runs a quest function when pressed) disable_palettex r:client_id restore_palettex r:client_id ... (X button now behaves normally) Arguments: labelB (for set_palettex_callback only) = function to call when X button is pressed",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(ILabelType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ACTIVATE_PALETTEX = Opcode(
    code = 0xF915,
    mnemonic = "activate_palettex",
    doc = "These opcodes are generally used in the following sequence: set_palettex_callback r:client_id, callback_label activate_palettex r:client_id enable_palettex r:client_id ... (X button now runs a quest function when pressed) disable_palettex r:client_id restore_palettex r:client_id ... (X button now behaves normally) Arguments: labelB (for set_palettex_callback only) = function to call when X button is pressed",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ENABLE_PALETTEX = Opcode(
    code = 0xF916,
    mnemonic = "enable_palettex",
    doc = "These opcodes are generally used in the following sequence: set_palettex_callback r:client_id, callback_label activate_palettex r:client_id enable_palettex r:client_id ... (X button now runs a quest function when pressed) disable_palettex r:client_id restore_palettex r:client_id ... (X button now behaves normally) Arguments: labelB (for set_palettex_callback only) = function to call when X button is pressed",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_RESTORE_PALETTEX = Opcode(
    code = 0xF917,
    mnemonic = "restore_palettex",
    doc = "These opcodes are generally used in the following sequence: set_palettex_callback r:client_id, callback_label activate_palettex r:client_id enable_palettex r:client_id ... (X button now runs a quest function when pressed) disable_palettex r:client_id restore_palettex r:client_id ... (X button now behaves normally) Arguments: labelB (for set_palettex_callback only) = function to call when X button is pressed",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DISABLE_PALETTEX = Opcode(
    code = 0xF918,
    mnemonic = "disable_palettex",
    doc = "These opcodes are generally used in the following sequence: set_palettex_callback r:client_id, callback_label activate_palettex r:client_id enable_palettex r:client_id ... (X button now runs a quest function when pressed) disable_palettex r:client_id restore_palettex r:client_id ... (X button now behaves normally) Arguments: labelB (for set_palettex_callback only) = function to call when X button is pressed",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_PALETTEX_ACTIVATED = Opcode(
    code = 0xF919,
    mnemonic = "get_palettex_activated",
    doc = "Checks if activate_palettex has been run for a player",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_PALETTEX_ENABLED = Opcode(
    code = 0xF91A,
    mnemonic = "get_palettex_enabled",
    doc = "Checks if activate_palettex has been run for a player. The ignored argument here seems to be a bug. At least one official quest uses this opcode preceded by only two arg_push opcodes, implying that it was intended to take two arguments, but the client really does only use quest_arg_list[0] and quest_arg_list[2]",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(IntType, "ignored", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_DISABLE_MOVEMENT2 = Opcode(
    code = 0xF91B,
    mnemonic = "disable_movement2",
    doc = "Disables/enables movement for a player. Unlike disable_movement1, this does not send 6x2C",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ENABLE_MOVEMENT2 = Opcode(
    code = 0xF91C,
    mnemonic = "enable_movement2",
    doc = "Disables/enables movement for a player. Unlike disable_movement1, this does not send 6x2C.",
    params = listOf(
        Param(IntType, "slot", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_TIME_PLAYED = Opcode(
    code = 0xF91D,
    mnemonic = "get_time_played",
    doc = "Returns the local player's play time in seconds.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_GUILDCARD_TOTAL = Opcode(
    code = 0xF91E,
    mnemonic = "get_guildcard_total",
    doc = "Returns the number of Guild Cards saved to the Guild Card file.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_SLOT_MESETA = Opcode(
    code = 0xF91F,
    mnemonic = "get_slot_meseta",
    doc = "Returns the amount of Meseta the player has in both their inventory and bank",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "returned Meseta amount in inventory", null, false, true),
            Param(IntType, "returned Meseta amount in bank", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_PLAYER_LEVEL = Opcode(
    code = 0xF920,
    mnemonic = "get_player_level",
    doc = "Returns a player's level",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_SECTION_ID = Opcode(
    code = 0xF921,
    mnemonic = "get_section_id",
    doc = "Returns a player's section ID",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_PLAYER_HP = Opcode(
    code = 0xF922,
    mnemonic = "get_player_hp",
    doc = "Returns a player's maximum and current HP and TP. If there's no player in the given slot, the returned values are all FFFFFFFF",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned maximum HP", null, false, true),
            Param(IntType, "returned current HP", null, false, true),
            Param(IntType, "returned maximum TP", null, false, true),
            Param(IntType, "returned current TP", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_FLOOR_NUMBER = Opcode(
    code = 0xF923,
    mnemonic = "get_floor_number",
    doc = "Returns the floor and room ID of the given player. If there's no player in the given slot, the returned values are both FFFFFFFF",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned floor number", null, false, true),
            Param(IntType, "returned room number", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_COORD_PLAYER_DETECT = Opcode(
    code = 0xF924,
    mnemonic = "get_coord_player_detect",
    doc = "Checks if each player (individually) is near the given location",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location [0]", "location (x, z as integers; y not included)", true, false),
            Param(IntType, "location [1]", "location (x, z as integers; y not included)", true, false),
            Param(IntType, "radius as integer", null, true, false),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, "results for each player [0]", "results for each player (0 = player not present or outside radius; 1 = player within radius)", true, false),
            Param(IntType, "results for each player [1]", "results for each player (0 = player not present or outside radius; 1 = player within radius)", true, false),
            Param(IntType, "results for each player [2]", "results for each player (0 = player not present or outside radius; 1 = player within radius)", true, false),
            Param(IntType, "results for each player [3]", "results for each player (0 = player not present or outside radius; 1 = player within radius)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_READ_GLOBAL_FLAG = Opcode(
    code = 0xF925,
    mnemonic = "read_global_flag",
    doc = "Reads the value of a quest counter",
    params = listOf(
        Param(IntType, "counter index", "counter index (0-15)", false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_WRITE_GLOBAL_FLAG = Opcode(
    code = 0xF926,
    mnemonic = "write_global_flag",
    doc = "Writes a value to a quest counter",
    params = listOf(
        Param(IntType, "counter index", "counter index (0-15)", false, false),
        Param(IntType, "value to write", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ITEM_DETECT_BANK2 = Opcode(
    code = 0xF927,
    mnemonic = "item_detect_bank2",
    doc = "Checks if an item exists in the local player's bank. The matching logic is the same as in find_inventory_item",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item.data1[0-2] [0]", null, false, true),
            Param(IntType, "item.data1[0-2] [1]", null, false, true),
            Param(IntType, "item.data1[0-2] [2]", null, false, true),
            Param(IntType, "item.data1[4]", null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_FLOOR_PLAYER_DETECT = Opcode(
    code = 0xF928,
    mnemonic = "floor_player_detect",
    doc = "Returns whether each player is present",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "returned flags [0]", "returned flags (for each player: 0 if absent, 1 if present)", false, true),
            Param(IntType, "returned flags [1]", "returned flags (for each player: 0 if absent, 1 if present)", false, true),
            Param(IntType, "returned flags [2]", "returned flags (for each player: 0 if absent, 1 if present)", false, true),
            Param(IntType, "returned flags [3]", "returned flags (for each player: 0 if absent, 1 if present)", false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_READ_DISK_FILE = Opcode(
    code = 0xF929,
    mnemonic = "read_disk_file",
    doc = "Prepares to load a GBA ROM from a local GSL file. Does nothing on Xbox and BB.",
    params = listOf(
        Param(StringType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_OPEN_PACK_SELECT = Opcode(
    code = 0xF92A,
    mnemonic = "open_pack_select",
    doc = "Opens a window for the player to choose an item.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_ITEM_SELECT = Opcode(
    code = 0xF92B,
    mnemonic = "item_select",
    doc = "Prevents the player from choosing a specific item in the item select window opened by open_pack_select. Generally used for subsequent item choices when trading multiple items",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "item ID", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_GET_ITEM_ID = Opcode(
    code = 0xF92C,
    mnemonic = "get_item_id",
    doc = "Returns the item chosen by the player in an open_pack_select window, or 0xFFFFFFFF if they canceled it.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_COLOR_CHANGE = Opcode(
    code = 0xF92D,
    mnemonic = "color_change",
    doc = "Adds a color overlay on the player's screen. The overlay fades in linearly over the given number of frames. The overlay is not deleted until the player changes areas or leaves the game, but it can be overwritten with another overlay with this same opcode. The overlay is under 2-dimensional objects like the HUD, pause menu, minimap, and text messages from the server, but is above everything else. valueA, valueB, valueC, valueD = red, green, blue, alpha components of color (00-FF each)",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, "fade speed", "fade speed (number of frames; 30 frames/sec)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SEND_STATISTIC = Opcode(
    code = 0xF92E,
    mnemonic = "send_statistic",
    doc = "Sends a statistic to the server via the AA command. The server is expected to respond with an AB command containing one of the label indexes set by prepare_statistic. The arguments to this opcode are sent verbatim in the params field of the AA command; the other fields in the AA command come from prepare_statistic.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GBA_WRITE_IDENTIFIERS = Opcode(
    code = 0xF92F,
    mnemonic = "gba_write_identifiers",
    doc = "Enables patching a GBA ROM before sending it to the GBA. valueA is ignored. If valueB is 1, the game writes two 32-bit values to the GBA ROM data before sending it: At offset 0x2C0, writes system_file->creation_timestamp At offset 0x2C4, writes current_time + rand(0, 100) current_time is in seconds since 00:00:00 on 1 January 2000. On Xbox and BB, this opcode does nothing.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CHAT_BOX = Opcode(
    code = 0xF930,
    mnemonic = "chat_box",
    doc = "Shows a message in a chat window. Can be closed with the winend opcode",
    params = listOf(
        Param(IntType, "X position on screen", null, false, false),
        Param(IntType, "Y position on screen", null, false, false),
        Param(IntType, "window width", null, false, false),
        Param(IntType, "window height", null, false, false),
        Param(IntType, "window style", "window style (0 = white window, 1 = chat box, anything else = no window, just text)", false, false),
        Param(StringType, "initial message", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CHAT_BUBBLE = Opcode(
    code = 0xF931,
    mnemonic = "chat_bubble",
    doc = "Shows a chat bubble linked to the given entity ID",
    params = listOf(
        Param(IntType, "entity", "entity (client ID, 0x1000 + enemy ID, or 0x4000 + object ID)", false, false),
        Param(StringType, "message", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SET_EPISODE2 = Opcode(
    code = 0xF932,
    mnemonic = "set_episode2",
    doc = "Sets the episode to be loaded the next time an area is loaded (e.g. by the player changing floors). regA is the same as for set_episode. Like set_episode, it resets the floor configuration to the defaults, but this happens at the time the player changes floors, not when the opcode is executed.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_ITEM_CREATE_MULTI_CM = Opcode(
    code = 0xF933,
    mnemonic = "item_create_multi_cm",
    doc = "Sets the rank prizes in offline challenge mode",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "rank", "rank (unusual value order: 0 = S, 1 = B, 2 = A)", false, true),
            Param(IntType, "item.data1[0-5] [0]", null, false, true),
            Param(IntType, "item.data1[0-5] [1]", null, false, true),
            Param(IntType, "item.data1[0-5] [2]", null, false, true),
            Param(IntType, "item.data1[0-5] [3]", null, false, true),
            Param(IntType, "item.data1[0-5] [4]", null, false, true),
            Param(IntType, "item.data1[0-5] [5]", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_SCROLL_TEXT = Opcode(
    code = 0xF934,
    mnemonic = "scroll_text",
    doc = "Shows a scrolling text window",
    params = listOf(
        Param(IntType, "X position on screen", null, false, false),
        Param(IntType, "Y position on screen", null, false, false),
        Param(IntType, "window width", null, false, false),
        Param(IntType, "window height", null, false, false),
        Param(IntType, "window style", "window style (same as for chat_box)", false, false),
        Param(FloatType, "scrolling speed", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(StringType, "message", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GBA_UNKNOWN1 = Opcode(
    code = 0xF935,
    mnemonic = "gba_unknown1",
    doc = "Creates, destroys, or updates the GBA loading progress bar (same as the quest download progress bar). These opcodes do nothing on Xbox and BB.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GBA_UNKNOWN2 = Opcode(
    code = 0xF936,
    mnemonic = "gba_unknown2",
    doc = "Creates, destroys, or updates the GBA loading progress bar (same as the quest download progress bar). These opcodes do nothing on Xbox and BB.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GBA_UNKNOWN3 = Opcode(
    code = 0xF937,
    mnemonic = "gba_unknown3",
    doc = "Creates, destroys, or updates the GBA loading progress bar (same as the quest download progress bar). These opcodes do nothing on Xbox and BB.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_ADD_DAMAGE_TO = Opcode(
    code = 0xF938,
    mnemonic = "add_damage_to",
    doc = "Damages a player",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(IntType, "amount", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ITEM_DELETE3 = Opcode(
    code = 0xF939,
    mnemonic = "item_delete3",
    doc = "Deletes an item from the local player's inventory. Like item_delete, but doesn't return anything",
    params = listOf(
        Param(IntType, "item ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_ITEM_INFO = Opcode(
    code = 0xF93A,
    mnemonic = "get_item_info",
    doc = "Returns the item data for an item chosen with open_pack_select. If the item doesn't exist, regsB[0] is set to 0xFFFFFFFF and the rest of regsB are unaffected",
    params = listOf(
        Param(IntType, "item ID", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned item.data1[0-11] [0]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [1]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [2]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [3]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [4]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [5]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [6]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [7]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [8]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [9]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [10]", null, false, true),
            Param(IntType, "returned item.data1[0-11] [11]", null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ITEM_PACKING1 = Opcode(
    code = 0xF93B,
    mnemonic = "item_packing1",
    doc = "Wraps an item in the player's inventory. The specified item is deleted and a new one is created with the wrapped flag set. The new item has a different item ID, which is not returned. Sends 6x29, then 6x26 if deleting the item causes the player's equipped weapon to no longer be usable, then 6x2B. It appears Sega did not intend for this to be used on BB, since the behavior wasn't changed - normally, item-related functions should be done by the server on BB, as the following opcode does",
    params = listOf(
        Param(IntType, "item ID", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ITEM_PACKING2 = Opcode(
    code = 0xF93C,
    mnemonic = "item_packing2",
    doc = "Like wrap_item, but also sets the present color. On BB, sends 6xD6, and the server should respond with the sequence of 6x29, 6x26 (if it was equipped), then 6xBE",
    params = listOf(
        Param(IntType, "item ID", null, false, false),
        Param(IntType, "present color", "present color (0-15; high 4 bits are masked out)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_LANG_SETTING = Opcode(
    code = 0xF93D,
    mnemonic = "get_lang_setting",
    doc = "Returns the local player's language setting. For values, see name_for_language in StaticGameData.cc.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_PREPARE_STATISTIC = Opcode(
    code = 0xF93E,
    mnemonic = "prepare_statistic",
    doc = "Sets some values to be sent to the server with send_statistic. starts, but that is overwritten by prepare_statistic",
    params = listOf(
        Param(IntType, "stat_id", "stat_id (used in send_statistic); this is set to the quest number from the header when the quest", false, false),
        Param(ILabelType, "label1", "label1 (used in send_statistic)", false, false),
        Param(ILabelType, "label2", "label2 (used in send_statistic)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_KEYWORD_DETECT = Opcode(
    code = 0xF93F,
    mnemonic = "keyword_detect",
    doc = "Enables use of the check_for_keyword opcode.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_KEYWORD = Opcode(
    code = 0xF940,
    mnemonic = "keyword",
    doc = "Checks if a word was said by a specific player. All of the same behaviors surrounding the message string apply as for set_chat_callback. Generally, this should be called every frame until the keyword is found (or the quest no longer cares if it is), since it will only return a match on the first frame that a string is said",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, "slot", null, false, false),
        Param(StringType, "string to match", "string to match (same semantics as for set_chat_callback)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_GUILDCARD_NUM = Opcode(
    code = 0xF941,
    mnemonic = "get_guildcard_num",
    doc = "Returns a player's Guild Card number",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_GET_RECENT_SYMBOL_CHAT = Opcode(
    code = 0xF942,
    mnemonic = "get_recent_symbol_chat",
    doc = "Returns the last symbol chat that a player said (at least, since the capture buffer was created). Use create_symbol_chat_capture_buffer before using this opcode. The data is returned as raw values in a sequence of 15 registers, in the same internal format as the game uses. On GC, this is SymbolChatBE; on all other platforms, it is SymbolChat (see SymbolChatT in PlayerSubordinates.hh for details)",
    params = listOf(
        Param(IntType, "client ID", null, false, false),
        Param(RegType(listOf(
            Param(IntType, "returned symbol chat data [0]", null, true, false),
            Param(IntType, "returned symbol chat data [1]", null, true, false),
            Param(IntType, "returned symbol chat data [2]", null, true, false),
            Param(IntType, "returned symbol chat data [3]", null, true, false),
            Param(IntType, "returned symbol chat data [4]", null, true, false),
            Param(IntType, "returned symbol chat data [5]", null, true, false),
            Param(IntType, "returned symbol chat data [6]", null, true, false),
            Param(IntType, "returned symbol chat data [7]", null, true, false),
            Param(IntType, "returned symbol chat data [8]", null, true, false),
            Param(IntType, "returned symbol chat data [9]", null, true, false),
            Param(IntType, "returned symbol chat data [10]", null, true, false),
            Param(IntType, "returned symbol chat data [11]", null, true, false),
            Param(IntType, "returned symbol chat data [12]", null, true, false),
            Param(IntType, "returned symbol chat data [13]", null, true, false),
            Param(IntType, "returned symbol chat data [14]", null, true, false),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_CREATE_SYMBOL_CHAT_CAPTURE_BUFFER = Opcode(
    code = 0xF943,
    mnemonic = "create_symbol_chat_capture_buffer",
    doc = "Creates the capture buffer required by get_recent_symbol_chat.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_GET_WRAP_STATUS = Opcode(
    code = 0xF944,
    mnemonic = "get_wrap_status",
    doc = "Checks whether an item is stackable",
    params = listOf(
        Param(IntType, "slot", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_INITIAL_FLOOR = Opcode(
    code = 0xF945,
    mnemonic = "initial_floor",
    doc = "Sets the floor where the players will start. This generally should be used in the start label (where map_designate, etc. are used). This is exactly the same as ba_initial_floor, except the floor numbers are not remapped: in Episode 1, 0 means Pioneer 2, 1 means Forest 1, etc. The warning on ba_initial_floor about common and rare item tables also applies to this opcode.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_SIN = Opcode(
    code = 0xF946,
    mnemonic = "sin",
    doc = "Computes the sine, cosine, or tangent of the input angle. Angles are in the range [0, 65535]",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, "input angle", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_COS = Opcode(
    code = 0xF947,
    mnemonic = "cos",
    doc = "Computes the sine, cosine, or tangent of the input angle. Angles are in the range [0, 65535].",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_TAN = Opcode(
    code = 0xF948,
    mnemonic = "tan",
    doc = "Computes the sine, cosine, or tangent of the input angle. Angles are in the range [0, 65535].",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_ATAN2_INT = Opcode(
    code = 0xF949,
    mnemonic = "atan2_int",
    doc = "Computes the arctangent of the input ratio",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(FloatType, "numerator as float", null, false, false),
        Param(FloatType, "denominator as float", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BOSS_IS_DEAD2 = Opcode(
    code = 0xF94A,
    mnemonic = "boss_is_dead2",
    doc = "Sets regA to 1 if Olga Flow has been defeated, or 0 otherwise.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_PARTICLE_EFFECT_NC = Opcode(
    code = 0xF94B,
    mnemonic = "particle_effect_nc",
    doc = "Creates a timed particle effect. Like particle_v3, but bypasses the camera-distance check that stops the emitter from creating new particles",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "location X", null, true, false),
            Param(IntType, "location Y", null, true, false),
            Param(IntType, "location Z", null, true, false),
            Param(IntType, "particle effect ID", null, true, false),
            Param(IntType, "duration", "duration (in frames; 30 frames/sec)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_PLAYER_EFFECT_NC = Opcode(
    code = 0xF94C,
    mnemonic = "player_effect_nc",
    doc = "Creates a particle effect at an entity's current position. Like particle_id_v3, but bypasses the camera-distance check that stops the emitter from creating new particles",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, "particle effect ID", null, true, false),
            Param(IntType, "duration in frames", null, true, false),
            Param(IntType, "entity", "entity (client ID, 0x1000 + enemy ID, or 0x4000 + object ID)", true, false),
            Param(IntType, "y offset", "y offset (as integer)", true, false),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_NOP_F94D = Opcode(
    code = 0xF94D,
    mnemonic = "nop_f94d",
    doc = "On BB this opcode is a no-op. Newserv documents four other variants of 0xF94D for other versions: has_ep3_save_file on GC PSO Plus (returns 1 if a PSO3_CHARACTER file is present), give_card / give_or_take_card on EP3, and unknown_F94D on Xbox.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NOP_F94E = Opcode(
    code = 0xF94E,
    mnemonic = "nop_f94e",
    doc = "These opcodes all do nothing on BB. F94D is presumably the voice chat opcode from Xbox, which was removed, but it's not clear what the other two might have originally been.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_NOP_F94F = Opcode(
    code = 0xF94F,
    mnemonic = "nop_f94f",
    doc = "These opcodes all do nothing on BB. F94D is presumably the voice chat opcode from Xbox, which was removed, but it's not clear what the other two might have originally been.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BB_P2_MENU = Opcode(
    code = 0xF950,
    mnemonic = "bb_p2_menu",
    doc = "Opens one of the Pioneer 2 counter menus, specified by valueA. Values: 0 = weapon shop 1 = medical center 2 = armor shop 3 = item shop 4 = Hunter's Guild quest counter 5 = bank 6 = tekker 7 = government quest counter 8 = Momoka item exchange (this opens a menu displaying the items specified in the quest header, which sends 6xD9 when the player chooses an item) valueA is not bounds-checked, so it could be used to write a byte with the value 1 anywhere in memory.",
    params = listOf(
        Param(IntType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_MAP_DESIGNATE = Opcode(
    code = 0xF951,
    mnemonic = "bb_map_designate",
    doc = "Behaves exactly the same as map_designate_ex, but the arguments are specified as five immediate bytes: floor, map, type, map variation, and object-set variation (the client's objset field). Type values: 0 = use quest DAT, 1 = use offline template, 2 = use online template, 3 = load no entities.",
    params = listOf(
        Param(ByteType, "floor number", null, false, false),
        Param(ByteType, "map ID", null, false, false),
        Param(ByteType, "type", "type (0: use layout, 1: use offline template, 2: use online template, 3: nothing)", false, false),
        Param(ByteType, "map variation", null, false, false),
        Param(ByteType, "object-set variation", "The client's objset field; selects the object, enemy, and event asset set.", false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BB_GET_NUMBER_IN_PACK = Opcode(
    code = 0xF952,
    mnemonic = "bb_get_number_in_pack",
    doc = "Returns the number of items in the player's inventory.",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BB_SWAP_ITEM = Opcode(
    code = 0xF953,
    mnemonic = "bb_swap_item",
    doc = "Requests an item exchange in the player's inventory. Sends 6xD5. The requested item must match one of the item creation masks in the quest script's header. valueA/valueB/valueC = item.data1[0-2] to search for valueD/valueE/valueF = item.data1[0-2] to replace it with",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_CHECK_WRAP = Opcode(
    code = 0xF954,
    mnemonic = "bb_check_wrap",
    doc = "Checks if an item can be wrapped",
    params = listOf(
        Param(IntType, "item ID", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PD_ITEM = Opcode(
    code = 0xF955,
    mnemonic = "bb_exchange_pd_item",
    doc = "Requests an item exchange for Photon Drops. Sends 6xD7. The requested item must match one of the item creation masks in the quest script's header. valueA/valueB/valueC = item.data1[0-2] for requested item",
    params = listOf(
        Param(IntType, "item.data1[0]", null, false, false),
        Param(IntType, "item.data1[1]", null, false, false),
        Param(IntType, "item.data1[2]", null, false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PD_SRANK = Opcode(
    code = 0xF956,
    mnemonic = "bb_exchange_pd_srank",
    doc = "Requests an S-rank special upgrade in exchange for Photon Drops. Sends 6xD8. valueB/valueC/valueD = item.data1[0-2]",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, "item.data1[0]", null, false, false),
        Param(IntType, "item.data1[1]", null, false, false),
        Param(IntType, "item.data1[2]", null, false, false),
        Param(IntType, null, null, false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PD_PERCENT = Opcode(
    code = 0xF957,
    mnemonic = "bb_exchange_pd_percent",
    doc = "Requests a weapon attribute upgrade in exchange for Photon Drops. Sends 6xDA. valueB/valueC/valueD = item.data1[0-2]",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, "item.data1[0]", null, false, false),
        Param(IntType, "item.data1[1]", null, false, false),
        Param(IntType, "item.data1[2]", null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, "payment count", "payment count (number of PDs)", false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PS_PERCENT = Opcode(
    code = 0xF958,
    mnemonic = "bb_exchange_ps_percent",
    doc = "Requests a weapon attribute upgrade in exchange for Photon Spheres. Sends 6xDA. Same arguments as bb_exchange_pd_percent, except Photon Spheres are used instead.",
    params = listOf(
        Param(IntType, null, null, false, false),
        Param(IntType, "item.data1[0]", null, false, false),
        Param(IntType, "item.data1[1]", null, false, false),
        Param(IntType, "item.data1[2]", null, false, false),
        Param(IntType, null, null, false, false),
        Param(IntType, "payment count", "payment count (number of PSes)", false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_SET_EP4_BOSS_CAN_ESCAPE = Opcode(
    code = 0xF959,
    mnemonic = "bb_set_ep4_boss_can_escape",
    doc = "Determines whether the Episode 4 boss can escape if undefeated after 20 minutes",
    params = listOf(
        Param(IntType, "boss can escape", "boss can escape (0 = no, 1 = yes (default))", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_IS_EP4_BOSS_DYING = Opcode(
    code = 0xF95A,
    mnemonic = "bb_is_ep4_boss_dying",
    doc = "Returns 1 if the Episode 4 boss death cutscene is playing, or 0 if not (even if the boss is already defeated).",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val OP_BB_SEND_6XD9 = Opcode(
    code = 0xF95B,
    mnemonic = "bb_send_6xd9",
    doc = "Requests an item exchange. Sends 6xD9. The requested item must match one of the item creation masks in the quest script's header",
    params = listOf(
        Param(IntType, "find_item.data1[0-2]", "find_item.data1[0-2] (low 3 bytes; high byte unused)", false, false),
        Param(IntType, "replace_item.data1[0-2]", "replace_item.data1[0-2] (low 3 bytes; high byte unused)", false, false),
        Param(IntType, "token1", "token1 (see 6xD9 in CommandFormats.hh)", false, false),
        Param(IntType, "token2", "token2 (see 6xD9 in CommandFormats.hh)", false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_SLT = Opcode(
    code = 0xF95C,
    mnemonic = "bb_exchange_slt",
    doc = "Requests an exchange of Secret Lottery Tickets for items. Sends 6xDE. The pool of items that can be returned by this opcode is determined by the quest's header; newserv assembles/disassembles this field as .allow_create_item directives. The first entry in the header is the currency item (which is to be deleted from the inventory); the others are the items the server randomly chooses from",
    params = listOf(
        Param(IntType, "index", null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", "label to call on failure (unused because of a client bug; see 6xDE description in CommandFormats.hh)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PC = Opcode(
    code = 0xF95D,
    mnemonic = "bb_exchange_pc",
    doc = "Removes a Photon Crystal from the player's inventory, and disables drops for the rest of the quest. Sends 6xDF.",
    params = emptyList(),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.None,
)
val OP_BB_BOX_CREATE_BP = Opcode(
    code = 0xF95E,
    mnemonic = "bb_box_create_bp",
    doc = "Requests an item drop within a quest. Sends 6xE0. valueB/valueC = x, z coordinates as floats",
    params = listOf(
        Param(IntType, "type", "type (corresponds to QuestF95EResultItems in config.json)", false, false),
        Param(FloatType, null, null, false, false),
        Param(FloatType, null, null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_EXCHANGE_PT = Opcode(
    code = 0xF95F,
    mnemonic = "bb_exchange_pt",
    doc = "Requests an exchange of Photon Tickets for items. Sends 6xE1. 0 = success 1 = player doesn't have enough Photon Tickets 2 = inventory is full 3, 4, or 5 = \"server send error\" (from Gallon's Plan script; newserv never sends these)",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
        Param(IntType, "result index", "result index (index into QuestF95FResultItems in config.json)", false, false),
        Param(ILabelType, "label to call on success", null, false, false),
        Param(ILabelType, "label to call on failure", null, false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_SEND_6XE2 = Opcode(
    code = 0xF960,
    mnemonic = "bb_send_6xe2",
    doc = "Requests a prize from the Meseta gambling prize list. Sends 6xE2. The server responds with 6xE3, which sets the <meseta_slot_prize> replacement token in message strings. The status of this can be checked with bb_get_6xE3_status",
    params = listOf(
        Param(IntType, "result tier", "result tier (see QuestF960SuccessResultItems and QuestF960FailureResultItems in config.json)", false, false),
    ),
    stack = StackInteraction.Pop,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Stack,
)
val OP_BB_GET_6XE3_STATUS = Opcode(
    code = 0xF961,
    mnemonic = "bb_get_6xe3_status",
    doc = "Returns the status of the expected 6xE3 command from a preceding bb_send_6xE2 opcode. Return values: 0 = 6xE3 hasn't been received 1 = the received item is valid 2 = the received item is invalid, or the item ID was already in use",
    params = listOf(
        Param(RegType(listOf(
            Param(IntType, null, null, false, true),
        ), 1), null, null, false, false),
    ),
    stack = null,
    varargs = false,
    known = true,
    versionMask = 0xFF,
    argsMode = ArgsMode.Inline,
)
val ALL_OPCODES: List<Opcode> = listOf(
    OP_NOP,
    OP_RET,
    OP_SYNC,
    OP_EXIT,
    OP_THREAD,
    OP_VA_START_V3_V4,
    OP_VA_END_V3_V4,
    OP_VA_CALL_V3_V4,
    OP_LET,
    OP_LETI,
    OP_LETA_V0_V2,
    OP_LETB_V3_V4,
    OP_LETW,
    OP_LETA,
    OP_LETO,
    OP_SET,
    OP_CLEAR,
    OP_REV,
    OP_GSET,
    OP_GCLEAR,
    OP_GREV,
    OP_GLET,
    OP_GGET,
    OP_ADD,
    OP_ADDI,
    OP_SUB,
    OP_SUBI,
    OP_MUL,
    OP_MULI,
    OP_DIV,
    OP_DIVI,
    OP_AND,
    OP_ANDI,
    OP_OR,
    OP_ORI,
    OP_XOR,
    OP_XORI,
    OP_MOD,
    OP_MODI,
    OP_JMP,
    OP_CALL,
    OP_JMP_ON,
    OP_JMP_OFF,
    OP_JMP_E,
    OP_JMPI_E,
    OP_JMP_NE,
    OP_JMPI_NE,
    OP_UJMP_G,
    OP_UJMPI_G,
    OP_JMP_G,
    OP_JMPI_G,
    OP_UJMP_L,
    OP_UJMPI_L,
    OP_JMP_L,
    OP_JMPI_L,
    OP_UJMP_GE,
    OP_UJMPI_GE,
    OP_JMP_GE,
    OP_JMPI_GE,
    OP_UJMP_LE,
    OP_UJMPI_LE,
    OP_JMP_LE,
    OP_JMPI_LE,
    OP_SWITCH_JMP,
    OP_SWITCH_CALL,
    OP_NOP_42_V0_V2,
    OP_STACK_PUSH_V3_V4,
    OP_STACK_POP_V3_V4,
    OP_STACK_PUSHM_V3_V4,
    OP_STACK_POPM_V3_V4,
    OP_ARG_PUSHR_V3_V4,
    OP_ARG_PUSHL_V3_V4,
    OP_ARG_PUSHB_V3_V4,
    OP_ARG_PUSHW_V3_V4,
    OP_ARG_PUSHA_V3_V4,
    OP_ARG_PUSHO_V3_V4,
    OP_ARG_PUSHS_V3_V4,
    OP_UNKNOWN_4F,
    OP_MESSAGE,
    OP_LIST,
    OP_FADEIN,
    OP_FADEOUT,
    OP_SE,
    OP_BGM,
    OP_NOP_56,
    OP_NOP_57,
    OP_ENABLE,
    OP_DISABLE,
    OP_WINDOW_MSG,
    OP_ADD_MSG,
    OP_MESEND,
    OP_GETTIME,
    OP_WINEND,
    OP_NPC_CRT_V3,
    OP_NPC_STOP,
    OP_NPC_PLAY,
    OP_NPC_KILL,
    OP_NPC_NONT,
    OP_NPC_TALK,
    OP_NPC_CRP_V0_V2,
    OP_NPC_CRP_V3_V3_V4,
    OP_CREATE_PIPE,
    OP_P_HPSTAT_V3,
    OP_P_DEAD_V3,
    OP_P_DISABLEWARP,
    OP_P_ENABLEWARP,
    OP_P_MOVE_V0_V2,
    OP_P_MOVE_V3_V3_V4,
    OP_P_LOOK,
    OP_P_ACTION_DISABLE,
    OP_P_ACTION_ENABLE,
    OP_DISABLE_MOVEMENT1,
    OP_ENABLE_MOVEMENT1,
    OP_P_NONCOL,
    OP_P_COL,
    OP_P_SETPOS,
    OP_P_RETURN_GUILD,
    OP_P_TALK_GUILD,
    OP_NPC_TALK_PL_V0_V2,
    OP_NPC_TALK_PL_V3_V3_V4,
    OP_NPC_TALK_KILL,
    OP_NPC_CRTPK,
    OP_NPC_CRPPK_V0_V2,
    OP_NPC_CRPPK_V3_V3_V4,
    OP_NPC_CRPTALK_V0_V2,
    OP_NPC_CRPTALK_V3_V3_V4,
    OP_P_LOOK_AT_V1,
    OP_NPC_CRP_ID_V0_V2,
    OP_NPC_CRP_ID_V3_V3_V4,
    OP_CAM_QUAKE,
    OP_CAM_ADJ,
    OP_CAM_ZMIN,
    OP_CAM_ZMOUT,
    OP_CAM_PAN_V0_V2,
    OP_CAM_PAN_V3_V3_V4,
    OP_GAME_LEV_SUPER,
    OP_GAME_LEV_RESET,
    OP_POS_PIPE_V0_V2,
    OP_POS_PIPE_V3_V3_V4,
    OP_IF_ZONE_CLEAR,
    OP_CHK_ENE_NUM,
    OP_UNHIDE_OBJ,
    OP_UNHIDE_ENE,
    OP_AT_COORDS_CALL,
    OP_AT_COORDS_TALK,
    OP_COL_NPCIN,
    OP_COL_NPCINR,
    OP_SWITCH_ON,
    OP_SWITCH_OFF,
    OP_PLAYBGM_EPI,
    OP_SET_MAINWARP,
    OP_SET_OBJ_PARAM,
    OP_SET_FLOOR_HANDLER_V0_V2,
    OP_SET_FLOOR_HANDLER_V3_V4,
    OP_CLR_FLOOR_HANDLER,
    OP_COL_PLINAW,
    OP_HUD_HIDE,
    OP_HUD_SHOW,
    OP_CINE_ENABLE,
    OP_CINE_DISABLE,
    OP_NOP_A0_DEBUG,
    OP_SET_QT_FAILURE_V0_V2,
    OP_SET_QT_FAILURE_V3_V4,
    OP_SET_QT_SUCCESS_V0_V2,
    OP_SET_QT_SUCCESS_V3_V4,
    OP_CLR_QT_FAILURE,
    OP_CLR_QT_SUCCESS,
    OP_SET_QT_CANCEL_V0_V2,
    OP_SET_QT_CANCEL_V3_V4,
    OP_CLR_QT_CANCEL,
    OP_PL_WALK_V0_V2,
    OP_PL_WALK_V3_V3_V4,
    OP_PL_ADD_MESETA,
    OP_THREAD_STG,
    OP_DEL_OBJ_PARAM,
    OP_ITEM_CREATE,
    OP_ITEM_CREATE2,
    OP_ITEM_DELETE,
    OP_ITEM_DELETE2,
    OP_ITEM_CHECK,
    OP_SETEVT,
    OP_GET_DIFFLVL,
    OP_SET_QT_EXIT_V1_V2,
    OP_SET_QT_EXIT_V3_V4,
    OP_CLR_QT_EXIT,
    OP_NOP_BC,
    OP_PARTICLE_V1_V2,
    OP_PARTICLE_V3_V3_V4,
    OP_NPC_TEXT,
    OP_NPC_CHKWARP,
    OP_PL_PKOFF,
    OP_MAP_DESIGNATE,
    OP_MASTERKEY_ON,
    OP_MASTERKEY_OFF,
    OP_WINDOW_TIME,
    OP_WINEND_TIME,
    OP_WINSET_TIME,
    OP_GETMTIME,
    OP_SET_QUEST_BOARD_HANDLER_V1_V2,
    OP_SET_QUEST_BOARD_HANDLER_V3_V4,
    OP_CLEAR_QUEST_BOARD_HANDLER,
    OP_PARTICLE_ID_V1_V2,
    OP_PARTICLE_ID_V3_V3_V4,
    OP_NPC_CRPTALK_ID_V1_V2,
    OP_NPC_CRPTALK_ID_V3_V3_V4,
    OP_NPC_LANG_CLEAN,
    OP_PL_PKON,
    OP_PL_CHK_ITEM2,
    OP_ENABLE_MAINMENU,
    OP_DISABLE_MAINMENU,
    OP_START_BATTLEBGM,
    OP_END_BATTLEBGM,
    OP_DISP_MSG_QB,
    OP_CLOSE_MSG_QB,
    OP_SET_EVENTFLAG_V3,
    OP_SYNC_REGISTER_V1_V2,
    OP_SYNC_REGISTER_V3_V4,
    OP_SET_RETURNHUNTER,
    OP_SET_RETURNCITY,
    OP_LOAD_PVR,
    OP_LOAD_MIDI,
    OP_UNKNOWN_DE,
    OP_NPC_PARAM_V1_V2,
    OP_NPC_PARAM_V3_V3_V4,
    OP_PAD_DRAGON,
    OP_CLEAR_MAINWARP,
    OP_PCAM_PARAM_V1_V2,
    OP_PCAM_PARAM_V3_V3_V4,
    OP_START_SETEVT,
    OP_WARP_ON,
    OP_WARP_OFF,
    OP_GET_SLOTNUMBER,
    OP_GET_SERVERNUMBER,
    OP_SET_EVENTFLAG2,
    OP_RES,
    OP_MODI2_V1_V4,
    OP_ENABLE_BGMCTRL,
    OP_SW_SEND,
    OP_CREATE_BGMCTRL,
    OP_PL_ADD_MESETA2,
    OP_SYNC_REGISTER2_V1_V2,
    OP_SYNC_REGISTER2_V3_V4,
    OP_SEND_REGWORK,
    OP_LETI_FIXED_CAMERA_V2,
    OP_LETI_FIXED_CAMERA_V3_V3_V4,
    OP_DEFAULT_CAMERA_POS1,
    OP_GET_GC_NUMBER,
    OP_UNKNOWN_FB,
    OP_DEBUG_F800,
    OP_SET_CHAT_CALLBACK,
    OP_GET_DIFFICULTY_LEVEL2,
    OP_GET_NUMBER_OF_PLAYER1,
    OP_GET_COORD_OF_PLAYER,
    OP_ENABLE_MAP,
    OP_DISABLE_MAP,
    OP_MAP_DESIGNATE_EX,
    OP_DISABLE_WEAPON_DROP,
    OP_ENABLE_WEAPON_DROP,
    OP_BA_INITIAL_FLOOR,
    OP_SET_BA_RULES,
    OP_BA_SET_TECH,
    OP_BA_SET_EQUIP,
    OP_BA_SET_MAG,
    OP_BA_SET_ITEM,
    OP_BA_SET_TRAPMENU,
    OP_BA_SET_UNUSED_F817,
    OP_BA_SET_RESPAWN,
    OP_BA_SET_CHAR,
    OP_BA_DROPWEP,
    OP_BA_TEAMS,
    OP_BA_DISP_MSG,
    OP_DEATH_LVL_UP,
    OP_BA_SET_MESETA,
    OP_CMODE_STAGE,
    OP_NOP_F821,
    OP_NOP_F822,
    OP_SET_CMODE_CHAR_TEMPLATE,
    OP_SET_CMODE_DIFF,
    OP_EXP_MULTIPLICATION,
    OP_EXP_DIVISION,
    OP_GET_USER_IS_DEAD,
    OP_GO_FLOOR,
    OP_GET_NUM_KILLS,
    OP_RESET_KILLS,
    OP_UNLOCK_DOOR2,
    OP_LOCK_DOOR2,
    OP_IF_SWITCH_NOT_PRESSED,
    OP_IF_SWITCH_PRESSED,
    OP_UNKNOWN_F82F,
    OP_CONTROL_DRAGON,
    OP_RELEASE_DRAGON,
    OP_SHRINK,
    OP_UNSHRINK,
    OP_SET_SHRINK_CAM1,
    OP_SET_SHRINK_CAM2,
    OP_DISPLAY_CLOCK2,
    OP_SET_AREA_TOTAL,
    OP_DELETE_AREA_TITLE,
    OP_LOAD_NPC_DATA,
    OP_GET_NPC_DATA,
    OP_GIVE_DAMAGE_SCORE,
    OP_TAKE_DAMAGE_SCORE,
    OP_UNK_SCORE_F84A,
    OP_UNK_SCORE_F84B,
    OP_KILL_SCORE,
    OP_DEATH_SCORE,
    OP_UNK_SCORE_F84E,
    OP_ENEMY_DEATH_SCORE,
    OP_MESETA_SCORE,
    OP_BA_SET_TRAP,
    OP_BA_HIDE_TARGET_RETICLE,
    OP_REVERSE_WARPS,
    OP_UNREVERSE_WARPS,
    OP_SET_ULT_MAP,
    OP_UNSET_ULT_MAP,
    OP_SET_AREA_TITLE,
    OP_BA_SHOW_SELF_TRAPS,
    OP_BA_HIDE_SELF_TRAPS,
    OP_EQUIP_ITEM_V2,
    OP_EQUIP_ITEM_V3_V4,
    OP_UNEQUIP_ITEM,
    OP_P_TALK_GUILD_LOCAL,
    OP_SET_ALLOW_ITEM_FLAGS,
    OP_BA_ENABLE_SONAR,
    OP_BA_SONAR_COUNT,
    OP_CLEAR_SCORE_ANNOUNCE,
    OP_SET_SCORE_ANNOUNCE,
    OP_GIVE_S_RANK_WEAPON_V2,
    OP_GIVE_S_RANK_WEAPON_V3_V4,
    OP_GET_MAG_LEVELS_V2,
    OP_GET_MAG_LEVELS_V3_V4,
    OP_CMODE_RANK,
    OP_AWARD_ITEM_NAME,
    OP_AWARD_ITEM_SELECT,
    OP_AWARD_ITEM_GIVE_TO,
    OP_SET_CMODE_RANK,
    OP_CHECK_RANK_TIME,
    OP_ITEM_CREATE_CMODE,
    OP_BA_BOX_DROPS,
    OP_AWARD_ITEM_OK,
    OP_BA_SET_TRAPSELF,
    OP_BA_CLEAR_TRAPSELF,
    OP_BA_SET_LIVES,
    OP_BA_SET_TECH_LVL,
    OP_BA_SET_LVL,
    OP_BA_SET_TIME_LIMIT,
    OP_BOSS_IS_DEAD,
    OP_SET_CMODE_RANK_OVERRIDE,
    OP_ENABLE_STEALTH_SUIT_EFFECT,
    OP_DISABLE_STEALTH_SUIT_EFFECT,
    OP_ENABLE_TECHS,
    OP_DISABLE_TECHS,
    OP_GET_GENDER,
    OP_GET_CHARA_CLASS,
    OP_TAKE_SLOT_MESETA,
    OP_GET_GUILD_CARD_FILE_CREATION_TIME,
    OP_KILL_PLAYER,
    OP_GET_SERIAL_NUMBER_V2_V3,
    OP_RETURN_0_F87E_V4,
    OP_READ_GUILDCARD_FLAG,
    OP_SET_TRAP_DAMAGE,
    OP_GET_PL_NAME,
    OP_GET_PL_JOB,
    OP_GET_PLAYER_PROXIMITY,
    OP_SET_EVENTFLAG16_V2,
    OP_SET_EVENTFLAG16_V3_V4,
    OP_SET_EVENTFLAG32_V2,
    OP_SET_EVENTFLAG32_V3_V4,
    OP_BA_GET_PLACE,
    OP_BA_GET_SCORE,
    OP_BA_CLOSE_MSG,
    OP_DISABLE_WIN_PFX,
    OP_GET_PLAYER_STATUS,
    OP_SEND_MAIL,
    OP_ONLINE_CHECK,
    OP_CHL_SET_TIMERECORD_V2_V3,
    OP_CHL_SET_TIMERECORD_V4,
    OP_CHL_GET_TIMERECORD,
    OP_SET_CMODE_GRAVE_RATES,
    OP_CLEAR_MAINWARP_ALL,
    OP_LOAD_ENEMY_DATA,
    OP_GET_PHYSICAL_DATA,
    OP_GET_ATTACK_DATA,
    OP_GET_RESIST_DATA,
    OP_GET_MOVEMENT_DATA,
    OP_GET_EVENTFLAG16,
    OP_GET_EVENTFLAG32,
    OP_SHIFT_LEFT,
    OP_SHIFT_RIGHT,
    OP_GET_RANDOM,
    OP_RESET_MAP,
    OP_DISP_CHL_RETRY_MENU,
    OP_CHL_REVERSER,
    OP_BA_FORBID_SCAPE_DOLLS,
    OP_PLAYER_RECOVERY,
    OP_DISABLE_BOSSWARP_OPTION,
    OP_ENABLE_BOSSWARP_OPTION,
    OP_IS_BOSSWARP_OPT_DISABLED,
    OP_LOAD_SERIAL_NUMBER_TO_FLAG_BUF,
    OP_WRITE_FLAG_BUF_TO_EVENT_FLAGS,
    OP_SET_CHAT_CALLBACK_NO_FILTER,
    OP_SET_SYMBOL_CHAT_COLLISION,
    OP_SET_SHRINK_SIZE,
    OP_DEATH_TECH_LVL_UP2,
    OP_VOL_OPT_IS_DEAD,
    OP_IS_THERE_GRAVE_MESSAGE,
    OP_GET_BA_RECORD,
    OP_GET_CMODE_PRIZE_RANK,
    OP_GET_NUMBER_OF_PLAYER2,
    OP_PARTY_HAS_NAME,
    OP_SOMEONE_HAS_SPOKEN,
    OP_READ1_V2,
    OP_READ1_V3_V4,
    OP_READ2_V2,
    OP_READ2_V3_V4,
    OP_READ4_V2,
    OP_READ4_V3_V4,
    OP_WRITE1_V2,
    OP_WRITE1_V3_V4,
    OP_WRITE2_V2,
    OP_WRITE2_V3_V4,
    OP_WRITE4_V2,
    OP_WRITE4_V3_V4,
    OP_CHECK_FOR_HACKING,
    OP_CHL_SET_MIN_TIME_ONLINE,
    OP_DISABLE_RETRY_MENU,
    OP_CHL_RECOVERY,
    OP_LOAD_GUILD_CARD_FILE_CREATION_TIME_TO_FLAG_BUF,
    OP_WRITE_FLAG_BUF_TO_EVENT_FLAGS2,
    OP_GET_PLAYER_GUILD_CARD_NUMBER_V2,
    OP_SET_EPISODE_V3_V4,
    OP_GET_CURRENT_SYMBOL_CHAT,
    OP_CREATE_SYMBOL_CHAT_MONITOR,
    OP_SAVE_SYSTEM_AND_GC_FILES,
    OP_FILE_DL_REQ,
    OP_GET_DL_STATUS_V3,
    OP_NOP_F8C1_V4,
    OP_GBA_UNKNOWN4,
    OP_GET_GBA_STATE,
    OP_CONGRATS_MSG_MULTI_CM,
    OP_STAGE_IN_PROGRESS_MULTI_CM_V3,
    OP_NOP_F8C5_V4,
    OP_QEXIT,
    OP_USE_ANIMATION,
    OP_STOP_ANIMATION,
    OP_RUN_TO_COORD,
    OP_SET_SLOT_INVINCIBLE,
    OP_SET_SLOT_TARGETABLE,
    OP_SET_SLOT_POISON,
    OP_SET_SLOT_PARALYZE,
    OP_SET_SLOT_SHOCK,
    OP_SET_SLOT_FREEZE,
    OP_SET_SLOT_SLOW,
    OP_SET_SLOT_CONFUSE,
    OP_SET_SLOT_SHIFTA,
    OP_SET_SLOT_DEBAND,
    OP_SET_SLOT_JELLEN,
    OP_SET_SLOT_ZALURE,
    OP_FLETI_FIXED_CAMERA,
    OP_FLETI_LOCKED_CAMERA,
    OP_DEFAULT_CAMERA_POS2,
    OP_SET_MOTION_BLUR,
    OP_SET_SCREEN_BW,
    OP_GET_VECTOR_FROM_PATH,
    OP_NPC_ACTION_STRING,
    OP_GET_PAD_COND,
    OP_GET_BUTTON_COND,
    OP_FREEZE_ENEMIES,
    OP_UNFREEZE_ENEMIES,
    OP_FREEZE_EVERYTHING,
    OP_UNFREEZE_EVERYTHING,
    OP_RESTORE_HP,
    OP_RESTORE_TP,
    OP_CLOSE_CHAT_BUBBLE,
    OP_MOVE_COORDS_OBJECT,
    OP_AT_COORDS_CALL_EX,
    OP_AT_COORDS_TALK_EX,
    OP_NPC_COORDS_CALL_EX,
    OP_PARTY_COORDS_CALL_EX,
    OP_SET_OBJ_PARAM_EX,
    OP_NPC_CHECK_STRAGGLE_EX,
    OP_ANIMATION_CHECK,
    OP_CALL_IMAGE_DATA,
    OP_NOP_F8EF,
    OP_TURN_OFF_BGM_P2,
    OP_TURN_ON_BGM_P2,
    OP_LOAD_UNK_DATA,
    OP_PARTICLE2,
    OP_DEC2FLOAT,
    OP_FLOAT2DEC,
    OP_FLET,
    OP_FLETI,
    OP_FADD,
    OP_FADDI,
    OP_FSUB,
    OP_FSUBI,
    OP_FMUL,
    OP_FMULI,
    OP_FDIV,
    OP_FDIVI,
    OP_GET_UNKNOWN_COUNT,
    OP_GET_STACKABLE_ITEM_COUNT,
    OP_FREEZE_AND_HIDE_EQUIP,
    OP_THAW_AND_SHOW_EQUIP,
    OP_SET_PALETTEX_CALLBACK,
    OP_ACTIVATE_PALETTEX,
    OP_ENABLE_PALETTEX,
    OP_RESTORE_PALETTEX,
    OP_DISABLE_PALETTEX,
    OP_GET_PALETTEX_ACTIVATED,
    OP_GET_PALETTEX_ENABLED,
    OP_DISABLE_MOVEMENT2,
    OP_ENABLE_MOVEMENT2,
    OP_GET_TIME_PLAYED,
    OP_GET_GUILDCARD_TOTAL,
    OP_GET_SLOT_MESETA,
    OP_GET_PLAYER_LEVEL,
    OP_GET_SECTION_ID,
    OP_GET_PLAYER_HP,
    OP_GET_FLOOR_NUMBER,
    OP_GET_COORD_PLAYER_DETECT,
    OP_READ_GLOBAL_FLAG,
    OP_WRITE_GLOBAL_FLAG,
    OP_ITEM_DETECT_BANK2,
    OP_FLOOR_PLAYER_DETECT,
    OP_READ_DISK_FILE,
    OP_OPEN_PACK_SELECT,
    OP_ITEM_SELECT,
    OP_GET_ITEM_ID,
    OP_COLOR_CHANGE,
    OP_SEND_STATISTIC,
    OP_GBA_WRITE_IDENTIFIERS,
    OP_CHAT_BOX,
    OP_CHAT_BUBBLE,
    OP_SET_EPISODE2,
    OP_ITEM_CREATE_MULTI_CM,
    OP_SCROLL_TEXT,
    OP_GBA_UNKNOWN1,
    OP_GBA_UNKNOWN2,
    OP_GBA_UNKNOWN3,
    OP_ADD_DAMAGE_TO,
    OP_ITEM_DELETE3,
    OP_GET_ITEM_INFO,
    OP_ITEM_PACKING1,
    OP_ITEM_PACKING2,
    OP_GET_LANG_SETTING,
    OP_PREPARE_STATISTIC,
    OP_KEYWORD_DETECT,
    OP_KEYWORD,
    OP_GET_GUILDCARD_NUM,
    OP_GET_RECENT_SYMBOL_CHAT,
    OP_CREATE_SYMBOL_CHAT_CAPTURE_BUFFER,
    OP_GET_WRAP_STATUS,
    OP_INITIAL_FLOOR,
    OP_SIN,
    OP_COS,
    OP_TAN,
    OP_ATAN2_INT,
    OP_BOSS_IS_DEAD2,
    OP_PARTICLE_EFFECT_NC,
    OP_PLAYER_EFFECT_NC,
    OP_NOP_F94D,
    OP_NOP_F94E,
    OP_NOP_F94F,
    OP_BB_P2_MENU,
    OP_BB_MAP_DESIGNATE,
    OP_BB_GET_NUMBER_IN_PACK,
    OP_BB_SWAP_ITEM,
    OP_BB_CHECK_WRAP,
    OP_BB_EXCHANGE_PD_ITEM,
    OP_BB_EXCHANGE_PD_SRANK,
    OP_BB_EXCHANGE_PD_PERCENT,
    OP_BB_EXCHANGE_PS_PERCENT,
    OP_BB_SET_EP4_BOSS_CAN_ESCAPE,
    OP_BB_IS_EP4_BOSS_DYING,
    OP_BB_SEND_6XD9,
    OP_BB_EXCHANGE_SLT,
    OP_BB_EXCHANGE_PC,
    OP_BB_BOX_CREATE_BP,
    OP_BB_EXCHANGE_PT,
    OP_BB_SEND_6XE2,
    OP_BB_GET_6XE3_STATUS
)

private val OPCODES_FOR: MutableMap<Version, OpcodeTables> = mutableMapOf()

fun opcodesFor(version: Version): OpcodeTables = OPCODES_FOR.getOrPut(version) {
    val byCode = arrayOfNulls<Opcode>(256)
    val byCodeF8 = arrayOfNulls<Opcode>(256)
    val byCodeF9 = arrayOfNulls<Opcode>(256)
    val bit = version.bit
    for (op in ALL_OPCODES) {
        if (op.versionMask and bit == 0) continue
        val idx = op.code and 0xFF
        val target = when {
            op.code <= 0xFF -> byCode
            op.code <= 0xF8FF -> byCodeF8
            else -> byCodeF9
        }
        check(target[idx] == null) {
            "duplicate (version=$version, code=0x${op.code.toString(16)}) for ${op.mnemonic}"
        }
        target[idx] = op
    }
    OpcodeTables(byCode, byCodeF8, byCodeF9)
}
